/**
 * Dex live scan session — Push 7 / v1.4.2 b3 / Build 8 / b13c.6.
 *
 * Aggregates per-frame `DexLiveFrameResult`s into a single live session
 * with running counters and dedupes repeated dex numbers across frames
 * before applying them to `dexStateStore`. Designed to be driven by a
 * React hook (`useDexLiveScan`) but kept framework-agnostic so the
 * test suite can drive it deterministically with synthetic frames.
 *
 * Application rules (deliberately strict):
 *   - Only frames with `mode: "grid_4x4"` and a non-null variant get
 *     applied — region overviews / unknown frames pass through to the
 *     diagnostic store but never mutate Dex state.
 *   - A (variant, dexNumber) observation is forwarded to
 *     `applyScanObservations` only once per session, even if the live
 *     OCR re-reads the same number on later frames. The store itself
 *     short-circuits no-op writes, but the session-level dedupe is
 *     what keeps the running `applied` counter honest.
 *   - Manual overrides are respected: the store skips them and we
 *     bump the session's `skippedManual` total accordingly.
 *
 * b13c.6 — PER-FRAME VARIANT ROUTING.
 *
 *   Each frame is routed by ITS OWN classifier variant. There is no
 *   session-wide variant lock and no debounced "current variant" that
 *   suppresses a frame whose variant differs from the last one. Every
 *   (variant, dexNumber) pair has its own independent dedupe + stability
 *   slot, so a scan that scrolls through lucky → perfect → purified →
 *   shadow commits each variant's catches under its own slot.
 *
 *   This reverses the Build 6/8 cross-variant machinery (debounce +
 *   commit-on-switch + applied-set seeding), which the Build 195 device
 *   log proved was silently DROPPING every non-first variant: combined
 *   with the (now-removed) sticky lock it forced all 4 panels into the
 *   single first-seen variant. The buffer-leak that machinery guarded
 *   against (the same on-screen ids being written under several variants
 *   as the trainer flips the PoGo filter) is now handled the simple way:
 *   each variant only ever sees the ids OCR actually read while that
 *   variant was the classifier's per-frame answer.
 *
 *   Writes are streamed LIVE: a (slot, variant) reading is committed to
 *   the store the moment it clears the confirmation gate (instant for a
 *   clean frame, or after a 2nd corroborating frame for a borderline one)
 *   — never batched to session end. Within a frame, rows are processed
 *   BOTTOM-UP (highest yPct first): the bottom rows sit furthest from the
 *   header occlusion and the ROI anchor, so they are the most stable read.
 */

import { applyScanObservations, getDexEntryState } from "./dexStateStore";
import type { NotCaughtReading } from "./dexReconciliation";
import { frameConfidence } from "./dexScanConfidence";
import { DEX_SCAN_VERBOSE, installDexScanLogTee } from "./dexScanLog";
import type { PokedexVariant, CellState } from "./pogoPokedexVariantClassifier";
import type { DexScanMode } from "./dexScanDiagnostics";
import type { DexLiveFrameResult } from "./dexLiveFrameAdapter";
import { SPRITE_APPLY_THRESHOLD } from "./dexLiveFrameAdapter";
import { createStickyPillCache } from "./stickyPillVariant";
import { createPillOcrCache, type PillOcrCache } from "./pillOcrCache";
import type { PokedexRegion } from "./pokedexOcr";
import { recordPillOcrSource } from "./scanPipelineTelemetry";
import {
  ShadowRosterSelfHealTracker,
  type SelfHealReport,
} from "./shadowRosterSelfHeal";
import { gateDexLiveFrame, type DexFrameGateReason, hasPokedexChrome } from "./dexFrameGate";
import { crossCheckCell } from "./dexCrossCheck";
import { filterScrollBackwardOutliers } from "./dexScrollWindow";
import { dexIdToRegion, dexRegionLabel, type OcrRegion } from "./dexIdToRegion";
import {
  createVariantStabilityTracker,
  type VariantStabilityTracker,
} from "./dexVariantStability";
import {
  createScanTelemetry,
  rowKeyForYPct,
  type ScanTelemetry,
  type ScanTelemetrySnapshot,
} from "./dexScanTelemetry";

/**
 * Push 1 / v1.4.3 b1 — stable-apply tuning.
 *
 * A (variant, dexNumber) must be observed across at least this many
 * frames before it is written to Dex state. This is the core first-run
 * accuracy fix: a single bad frame can no longer apply a spurious number.
 * The requirement is RELAXED to one frame when the OCR confidence is high
 * AND the grid signature is strong (a clean, unambiguous read shouldn't
 * make the user hold the camera for a second frame).
 *
 * Build 5 / v1.4.6 — RELAXED instant-apply thresholds (HISTORICAL). Build 4
 * captured only 27 of ~50 IDs because the old two-frame requirement dropped
 * every single-sighting ID, so instant-apply was lowered to the frame-gate
 * floor and a confident frame applied its numbers on first sighting.
 *
 * Run-3 / 2026-06-21 — REVERSED for PRESENT writes. The device telemetry proved
 * the instant-apply path is the single-frame false-write hole: every escaping
 * batch (Perfect [51,48,41,…], [42], [57,58,59,…]) came from ONE confidence:100
 * frame because instant-apply bypassed corroboration whenever confidence +
 * signature were high — which they always were. Present writes now ALWAYS
 * require STABLE_FRAMES_REQUIRED consecutive clean frames (Gate C, see the
 * ingest loop); the instant path is gone for them. A delayed real catch is
 * recovered by the next clean frame; a false write is permanent.
 *
 * HIGH_CONFIDENCE_INSTANT / STRONG_SIGNATURE_INSTANT are retained ONLY for the
 * not-caught reconciliation stream (`instantStable`), which decides how fast a
 * silhouette becomes a gap suggestion — never a dex write.
 */
const STABLE_FRAMES_REQUIRED = 2;

/**
 * Evidence-accumulation commit (2026-07 redesign — docs/scan-evidence-commit-
 * redesign.md). A cell commits on the BALANCE of its per-frame reads across the
 * frames it is naturally on screen during a scroll, not on one frame. This
 * replaces "single clean frame commits + a pile of single-frame vetoes"; the one
 * signal that separates a real catch from a silhouette/empty phantom is temporal
 * stability (a real catch reads caught in ~every frame; a phantom flickers).
 *
 * Votes per (variant, dexId):
 *   strong — caught verdict AND pixel spriteState "caught" (both classifiers agree)
 *   weak   — caught verdict, pixel state "silhouette" (could be a dark/grey catch)
 *   contra — a cell that reads NOT-caught (missing loop), OR a caught verdict whose
 *            pixel state is "empty"/"none" (density/pixel disagree ⇒ no body).
 *
 * 2026-07-22 — the empty→weak relaxation of pass 3 is REVERTED, on labelled
 * ground truth for the shiny dex (169 real catches, screenshots IMG_3724-3739).
 * It was meant to rescue dim catches the pixel classifier under-reads, but scored
 * against the labels it did not: recall FELL 92% -> 80% and silhouette rejection
 * fell 18/18 -> 14/18 in the same pass. Restoring empty/none -> contra, with the
 * substance floor below kept, scores 151/169 recall at 99.3% precision and 18/18
 * silhouettes on that trace — the best configuration measured. The dim-catch
 * recall it was chasing is real but has to come from fixing the sprite ROI, not
 * from spending the contra brake: see the row-1 note on EMPTY_STATE_APPLY_*.
 *
 * Commit tiers (TUNE ON DEVICE):
 *   Tier 1  (instant, keeps today's speed): strong >= 1 && contra === 0
 *   Tier 1b (flicker rescue):               strong >= 2  (commits despite contra)
 *   Tier 2  (gates the ambiguous middle):   votes >= EVIDENCE_MIN_VOTES
 *                                           && votes >= contra * EVIDENCE_CONTRA_RATIO
 * where votes = strong + weak. Tier 1b rescues a real catch that flickers to
 * "empty" during scroll (device #132: strong=7 buried under contra=12): two
 * independent density+pixel "caught" agreements can't be a phantom, which never
 * earns a single strong vote. A flickering caught<->silhouette cell that never
 * reaches strong>=2 still never clears; a genuinely-caught cell whose pixels read
 * "silhouette" accrues weak votes over a couple of scroll frames and commits.
 */
const EVIDENCE_MIN_VOTES = 2;
const EVIDENCE_CONTRA_RATIO = 2;

// EMPTY-STATE SUBSTANCE FLOOR (2026-07-22, shiny device pass at 2fps). A pure-
// density commit (strong === 0) whose committing frame reads spriteState "empty"
// is the residual density-over-call the doc's pass-3 flagged: at 2fps, blank
// cells on the shiny yellow screen accrue weak >= 3 (background bleed reads as a
// faint caught verdict) and false-commit — 17 this pass, ALL empty-state, tightly
// clustered at chroma 0.024–0.043 / foreground 778–1358. Real dim catches that
// ALSO read "empty" carry more colour OR mass (median chroma 0.094, fg 2859), so
// an empty-state pure-weak commit must show real substance: chroma above the
// floor OR foreground mass above it. Kills all 16 clustered false writes on this
// log at the cost of ~5 low-signal dim catches (recoverable by re-scan — the
// doc's "prefer a coverage gap over a phantom"). silhouette/none-state pure-weak
// commits are UNTOUCHED (zero false writes there this pass). TUNE ON DEVICE; the
// principled successor is the lumStd/bodyFrac structure signal now threaded onto
// the observation (telemetry) for the next pass.
const EMPTY_STATE_APPLY_CHROMA_FLOOR = 0.045;
const EMPTY_STATE_APPLY_FG_FLOOR = 1400;
/**
 * The same floor as a FRACTION of the sprite band's pixel area (2026-09-08).
 * 1400 px was calibrated on the owner's iPhone, whose broadcast frames are
 * 886x1918: a sprite band there is 212x173 = 36,676 px, so 1400 px is 0.038
 * of it. A 16:9 phone's frames carry ~40% of those pixels and the pixel
 * count would have gated real catches; the fraction does not move. The pixel
 * floor stays only as the fallback for a classification that carries no
 * fraction (older cached rows).
 */
export const EMPTY_STATE_APPLY_FG_FRACTION = 0.038;

/**
 * Does a pure-weak "empty"-state observation show enough substance (colour or
 * mass) to be a small dim catch rather than a blank cell? Exported for tests.
 */
export function emptyStateSubstanceOk(args: {
  chroma: number | null;
  fgFraction: number | null;
  fgPixels: number | null;
  bodyFrac?: number | null;
  lumStd?: number | null;
}): boolean {
  if ((args.chroma ?? 0) >= EMPTY_STATE_APPLY_CHROMA_FLOOR) return true;
  if (structuredBodyOk(args.bodyFrac, args.lumStd)) return true;
  if (args.fgFraction != null) return args.fgFraction >= EMPTY_STATE_APPLY_FG_FRACTION;
  return (args.fgPixels ?? 0) >= EMPTY_STATE_APPLY_FG_FLOOR;
}

/**
 * The body-structure classifier's OWN measure of substance (2026-09-23,
 * Galaxy shiny logs): a masked body covering at least this much of the sprite
 * band, with real internal luminance structure. Shiny Togetic (body 0.050,
 * lumStd 0.146, chroma 0.010, 402 px) and Chimecho (0.052 / 0.074 / 0.016 /
 * 465 px) are pale or thin sprites that fail BOTH the legacy hue test and the
 * chroma / mass floors above, so every look voted AGAINST them (Chimecho
 * 12 contra, never committed) although the presence classifier read a
 * structured body each time. Since the number box is masked (2026-09-22) a
 * blank card measures bodyFrac ~0, so a structured body is no longer the
 * phantom signature it was when the floors were calibrated.
 */
export const EMPTY_STATE_APPLY_BODY_FLOOR = 0.03;
export const EMPTY_STATE_APPLY_LUMSTD_FLOOR = 0.06;
export function structuredBodyOk(bodyFrac: number | null | undefined, lumStd: number | null | undefined): boolean {
  return (bodyFrac ?? 0) >= EMPTY_STATE_APPLY_BODY_FLOOR && (lumStd ?? 0) >= EMPTY_STATE_APPLY_LUMSTD_FLOOR;
}

/** Per-cell caught/contradiction tally for the evidence-accumulation commit. */
type CellEvidence = {
  strong: number;
  weak: number;
  contra: number;
  /** Frames in which the density verdict read this cell as caught. */
  frames: number;
  /** Frames in which the cell was held (not committed) after such a read. */
  held: number;
  /** Last grid slot the cell was read in (null until seen). */
  slot: number | null;
  /** The pixel classifier's reading on the LAST caught-verdict frame. */
  last: {
    state: string | null;
    conf: number;
    chroma: number | null;
    fg: number | null;
    body: number | null;
    lum: number | null;
  } | null;
  /** Why the last hold happened (see the hold log's `reason`). */
  lastReason: string | null;
};

/**
 * One row of the scan-end "held cells" summary (2026-09-23): a cell the
 * density verdict called caught at least once but the evidence engine never
 * committed. Emitted ALWAYS (not behind DEX_SCAN_VERBOSE) so an exported
 * release-build scan log says why a visible catch was missed: the 2026-09-22
 * Galaxy shiny pass held 713 reads and applied 16, and the log could not say
 * which cells or why.
 */
export type HeldCellSummary = {
  variant: PokedexVariant;
  dexId: number;
  slot: number | null;
  frames: number;
  held: number;
  strong: number;
  weak: number;
  contra: number;
  notCaughtReads: number;
  lastState: string | null;
  lastConf: number;
  lastChroma: number | null;
  lastFg: number | null;
  lastBody: number | null;
  lastLum: number | null;
  reason: string | null;
};
/** Cap on rows in the scan-end held-cells summary (one line each in the log). */
export const HELD_CELLS_SUMMARY_MAX = 150;
/**
 * Row-band tolerance (yPct) for the single-frame reading-order sort. Grid rows
 * sit ~0.16 apart; half a row separates them cleanly, so two observations within
 * this yPct band are treated as the SAME row and ordered left-to-right by
 * slotIndex, while a larger gap sorts strictly top-to-bottom.
 */
const ROW_BAND_EPSILON = 0.08;
/**
 * SETTLE WINDOW — how many calm frames must pass after the grid jumps before
 * the session is allowed to write again, and how far the frame's lead dex id
 * may move between reads before the move counts as a jump rather than a scroll.
 *
 * One frame is enough: the point is to skip the blended frames PoGo produces
 * while the list is still moving (its top-menu tuck yanks the grid without the
 * user touching it), not to make the trainer wait. At ~1 read frame/second a
 * hand-driven scroll advances the lead id by one row (4) and a brisk one by two;
 * 12 (three rows) is past anything a controlled scroll does between two frames
 * the stability gate accepted, so honest fast scrolling is not throttled.
 */
const SETTLE_FRAMES = 1;
const SETTLE_JUMP_MAX_IDS = 12;
/**
 * A REVERSAL costs two frames, not one.
 *
 * A forward jump lands on the page the trainer was already heading towards, so
 * one blended frame separates it from a clean read. A reversal is different in
 * kind: the list decelerates, stops and comes back, so the frame after the
 * turn is still carrying the outgoing page's pixels. Device trace 2026-07-20
 * dex #98 — the settle opened on `delta -8, reason "reverse"` with a budget of
 * one, and the very next frame committed #98/#100 off the blend. It is the one
 * false write in that scan with no silhouette history for the flip-flop guard
 * to catch, because nothing was wrong with the cell's history; the frame was
 * simply not a real read of the page it claimed to be.
 */
const SETTLE_FRAMES_AFTER_REVERSE = 2;
/**
 * FLIP-FLOP GUARD — how much not-caught history makes a cell "contested", and
 * how many independent caught reads it then takes to overturn that.
 *
 * Two not-caught reads is enough to mean it: a single one is noise, but the
 * cells this guard exists for read not-caught on a dozen consecutive frames
 * before one frame claimed otherwise. Two caught reads to overturn keeps the
 * cost to one extra frame for a cell that really is caught — the trainer is
 * still scrolling over it — while making a lone anomalous frame insufficient on
 * its own, which is the entire failure mode.
 *
 * WHAT COUNTS AS NOT-CAUGHT HISTORY (2026-07-21, Kanto+Johto shiny trace).
 * Only a positively MEASURED grey silhouette does — `spriteReason:
 * "silhouette-grey"`. A `sprite-absent` read is the classifier saying it found
 * nothing in the crop, which on a scrolling page usually means PoGo drew the
 * cell's number before its sprite. That is an absence of evidence, not evidence
 * of absence, and counting it would punish exactly the cells the trainer is
 * scrolling past fastest.
 *
 * The distinction is the whole guard. In that trace the two populations were
 * otherwise inseparable — no pixel feature and no repeat count told them apart
 * (#194, a real catch, read foreground 18338 / chroma 0.787 against #166, a
 * false write, at 16984 / 0.749; #235 got nine caught looks and was wrong,
 * #194 got one and was right) — but the KIND of prior not-caught read split
 * them perfectly:
 *
 *   false writes #120/#161/#165/#166/#235 → 2-4 `silhouette-grey` reads each
 *   real catches  #21/#193/#194           → 0; every prior read `sprite-absent`
 *
 * So this guard blocks five of the six false writes in that scan at zero cost
 * to recall. (#98 was not a flip-flop at all — it committed on a blended frame
 * one frame after a scroll reversal, which is the settle window's job.)
 */
const FLIPFLOP_MIN_NOT_CAUGHT_READS = 2;
const FLIPFLOP_CAUGHT_READS_REQUIRED = 2;
/**
 * Fast-scroll-pill-overlap apply guard (scan overhaul v2, Phase 4).
 *
 * NAMING: this gates on `pillOverlapFraction`, the fraction of a cell covered by
 * the iOS fast-scroll capsule (the right-edge "pill" from detectFastScrollPill),
 * NOT the Nexus PiP. The two are distinct overlays: the Nexus PiP is guarded
 * separately by cell-SKIP in the OCR layer (acceptCellNumberRoi `pip_obscured`
 * + the sprite-body overlay guard), which emits no observation — a PiP-obscured
 * cell never reaches this gate. Don't re-conflate them.
 *
 * Cells with more than this fraction covered by the pill were classified on
 * pill-blended pixels (the b18 mask removes the pill from the classifier, but a
 * heavily covered cell has too little sprite left to trust). They defer their
 * apply to a later frame where the pill has faded. 15% chosen as the smallest
 * threshold that eliminates the false-write class documented in device log
 * 2026-07-07 (dex #12/#15/#128/#144, pillOverlapFraction 0.32-0.61 on all four)
 * while still permitting cells with just a corner of the pill touching them to
 * apply normally. Below 15% the classifier output is empirically stable.
 */
const PILL_OVERLAP_APPLY_MAX = 0.15;
const HIGH_CONFIDENCE_INSTANT = 55;
/**
 * trial/simplify-gates round 2 (2026-07-06): the instant-vision apply path
 * uses this stricter floor rather than HIGH_CONFIDENCE_INSTANT. Vision returns
 * 100 on genuine clean reads; 95 gives a tiny cushion for anti-aliased edges
 * without permitting the 78-confidence partially-off-screen row that
 * false-applied #024 on Perfect during the 2026-07-06 device scan.
 * HIGH_CONFIDENCE_INSTANT (55) is retained for the LEGACY not-caught instant
 * corroboration path — do not conflate.
 */
const VISION_INSTANT_MIN_CONFIDENCE = 95;
// NOTE (merge 2026-07-22): main's EMPTY-CELL SMEAR FLOOR (a second
// EMPTY_STATE_APPLY_FG_FLOOR = 1000, gating the vision-instant tier) stood
// here. That tier is gone with the evidence redesign, and the surviving floor
// of the same name is the branch's, retuned to 1400 on the shiny 2fps pass.
// The 2026-07-20 ground truth it was fitted to is preserved in
// docs/scan-merge-superseded-main-guards.md.
const STRONG_SIGNATURE_INSTANT = 0.5;
// (2026-08-30 dead-code removal) Six constants and their doc blocks stood
// here: STREAK_GRACE_FRAMES (#006 single-frame streak grace) and the five
// Round-11/12/13 PURIFIED-only gates (ratio confirmation, sprite-confidence
// floor, slot-merge window, absence tolerance). All belonged to the
// Tesseract-era two-frame corroboration engine, which the evidence redesign
// deleted (see the note in ingest around the evidence loop); nothing
// referenced them any more, and their doc blocks were actively misleading -
// the variantResolutionConsolidation harness docstring cited the purified
// ratio gate as live behaviour long after it was gone.

/**
 * Build 6 / 2026-06-24 — STICKY-VARIANT CONTEXT HOLD for transient unknown
 * frames (Problem #1: "variant-context flicker starves confirmation streaks").
 *
 * Device telemetry showed the scan oscillating between a confidently locked
 * variant (perfect/purified) and lost context on a near-per-frame basis: runs
 * of up to 6 consecutive `mode:"unknown"` / `variant:null` frames interleaved
 * between good frames. A present write needs a 2–3 frame corroboration streak,
 * and the brief's intent is that a transient unknown frame must NOT drop the
 * in-flight streaks — the last-known (sticky) variant is HELD through it so the
 * streaks survive to accumulate across the surrounding good frames.
 *
 * This many consecutive unknown frames are tolerated as a context hold before
 * the session treats the variant as genuinely lost and lets its pending streaks
 * expire. It originally mirrored the dexScanContext MISS_BUDGET (4), but round-2
 * Shadow telemetry (2026-06-24) showed 84 `variant:null` reads still killing
 * in-flight streaks: the flicker gaps on a held Shadow page routinely run longer
 * than 4 frames, so pending streaks expired mid-confirmation and real catches
 * never applied. The budget is decoupled from MISS_BUDGET and raised to 8 to
 * bridge those observed gaps — long enough to survive the flicker, short enough
 * that a genuinely-departed page still releases its streaks within ~8 frames. A
 * real variant switch is unaffected (it hard-resets immediately via the
 * `variant_changed (sticky relock)` path below). Holding only PRESERVES existing
 * pending streaks for the sticky variant — it never creates new pending, never
 * increments a streak, and never applies — so it cannot manufacture a false
 * write (false writes remain the #1 failure class).
 */
const STICKY_VARIANT_HOLD_FRAMES = 8;

/**
 * Round-4 / 2026-06-24 — TIERED CONFIRMATION (Root Cause D).
 *
 * The default present-write bar stays STABLE_FRAMES_REQUIRED (2) and the weak
 * G-Max bar stays WEAK_GMAX_FRAMES_REQUIRED (3) — neither is raised. We ADD a
 * single-frame fast-path that LOWERS the bar to 1 ONLY for the safest possible
 * read, and a tier label for the medium band. The Purified scan applied just 4
 * of 168 cells partly because every clean catch had to survive two consecutive
 * frames through the variant flicker; a firmly-locked, very-high-confidence
 * caught sprite is reliable enough to apply on the frame it is first seen.
 *
 * Tiers (per the brief, mapped onto the existing bars so nothing is RAISED):
 *   - "single" (needed 1): ALL must hold — variant lock FIRMLY held (not freshly
 *     / transiently locked, see FIRM_VARIANT_LOCK_FRAMES), sprite verdict
 *     "caught", sprite confidence ≥ SINGLE_FRAME_SPRITE_CONFIDENCE, the cell
 *     passed the OCR ladder guard + curated-roster membership (implicit — it
 *     reached presentObs), and it is NOT weak G-Max evidence. Missing ANY one
 *     falls back to "two"/"three".
 *   - "two" (needed 2): the existing clean default, plus the medium band
 *     ("caught" + confidence ≥ MEDIUM_SPRITE_CONFIDENCE). Numerically identical
 *     to the prior default — only the telemetry label distinguishes them.
 *   - "three" (needed 3): weak G-Max / staged recovery sightings. Unchanged.
 */
const SINGLE_FRAME_SPRITE_CONFIDENCE = 0.9;
const MEDIUM_SPRITE_CONFIDENCE = 0.8;
/**
 * A variant lock counts as FIRMLY held — eligible for the single-frame
 * fast-path — only after the resolved variant has been the same across this many
 * consecutive good (gate- and stability-cleared) frames. A freshly (re)locked or
 * single-frame-flashed variant has a run of 1 and never qualifies, so the
 * fast-path can never fire on a transient lock. Sticky-hold flicker frames do
 * NOT reset the run (that is the Root Cause A hysteresis), so a real held lock
 * keeps its firm status across the unknown-frame gaps.
 */
const FIRM_VARIANT_LOCK_FRAMES = 3;

/**
 * Round-7 / 2026-06-24 — STRONG-histSim single-frame qualifier (Bug 2).
 *
 * The Round-4 single tier keys on sprite CONFIDENCE (≥0.9) + a firmly-held
 * variant lock. The Kanto Purified scan showed the very top of the dex
 * (#001 histSim 0.946, #002 0.636, #003 0.656) reach two-frame pending with a
 * single sighting but never get a 2nd consecutive frame — the fast scroll at the
 * top of the dex moves the row off-screen before it corroborates — so genuinely
 * recognised catches were lost to the streak requirement.
 *
 * A very high species histogram match is STRONGER evidence than the 0.9 sprite
 * confidence the single tier already trusts, so we add it as an ALTERNATIVE
 * single-frame qualifier: a "caught" sprite whose histSim is at/above this bar
 * applies on one strong frame even when the firm-lock / 0.9-confidence
 * conditions are not all met. 0.5 sits well above every noise/flicker source and
 * above the Round-7 Fix-1 zero-histSim phantom class (#024 at histSim 0), while
 * admitting #001/#002/#003 (0.6–0.95). It is purely ADDITIVE — no existing tier
 * qualifier is removed and nothing below this bar gets `needed` lowered.
 */
const STRONG_HISTSIM_SINGLE_FRAME = 0.5;

/**
 * Round-15 / 2026-06-25 — CAUGHT-STATE single-frame qualifier (additive).
 *
 * The Round-7 strong-histSim qualifier (≥0.5) recovers the very top of the dex
 * but is deliberately blind to sprite STATE: it fires on any "caught"-verdict
 * cell whose histSim clears 0.5, regardless of whether the underlying sprite was
 * a real caught body or a silhouette rescued by a chroma/presence heuristic.
 * Truly-sparse but genuinely-caught cells (R13: dex 31/34/114) read a real
 * species match in the 0.25–0.5 band that never reaches the 0.5 strong bar and,
 * during a fast scroll, never gets a 2nd corroborating frame — so they are lost.
 *
 * This adds a SECOND, LOWER single-frame bar that is ADDITIONALLY gated on
 * `spriteState === "caught"` — the discriminator the strong-histSim bar lacks.
 * The state gate is WHY the lower 0.25 histSim is safe: empty/silhouette cells
 * (which can carry a high histSim, e.g. a 0.501 silhouette) are excluded by
 * STATE, not by number, so no false write leaks in (R13: dex 99's best caught
 * frame reads 0.214 and is excluded with margin). It also keeps the existing
 * SINGLE_FRAME_SPRITE_CONFIDENCE (0.9) bar, so only a confident, caught-bodied,
 * species-matching cell single-frame applies. Purely ADDITIVE — no existing
 * tier qualifier is removed and no existing threshold is lowered. The histSim==0
 * veto is untouched: 0.25 sits far above 0, so a zero-histSim phantom can never
 * qualify here.
 */
const CAUGHT_STATE_SINGLE_FRAME_HISTSIM = 0.25;

/** Confirmation tier a present sighting cleared (telemetry + apply gating). */
type ApplyTier = "single" | "two" | "three";

/**
 * Push 4 / v1.4.3 b4 — honest cell counters.
 *
 * The Pokémon GO Pokédex shows a 4-column grid. On a phone-sized live
 * broadcast at most 4 full rows are visible, so a single frame can contain
 * at most 4×4 = 16 *candidate* cells. The previous diagnostics summed every
 * cell the legacy 3×6 sweep returned across EVERY frame (including rejected
 * overlay/non-grid frames), which is how the panel ended up claiming "379
 * cells observed" for what is physically a 16-cell screen. We now:
 *   - cap each accepted frame's candidate-cell contribution to this max, and
 *   - only accumulate cell/number counters for frames that pass the gate.
 */
const MAX_CELLS_PER_FRAME = 16;

export interface DexLiveSessionTotals {
  /** Frames the session has observed (any mode). */
  framesObserved: number;
  /** Frames whose mode was grid_4x4 with a non-null variant. */
  framesApplied: number;
  /** Distinct (variant, dexNumber) writes applied via the store. */
  applied: number;
  /** Distinct writes that hit a manual override and were skipped. */
  skippedManual: number;
  /** Per-reason counts of frames/numbers the gate or stability rule rejected. */
  gated: GatedReasonCounts;
  /**
   * G-Max QA aggregate — running totals of the PR #461 watched gate markers
   * across the whole session, plus the apply/frame context the monitor needs
   * to decide the "gate + apply" urgent-failure condition. Kept separate from
   * `gated` (which the diagnostics panel + existing tests depend on) so the QA
   * markers can evolve without perturbing user-facing diagnostics.
   */
  gmaxQa: GmaxQaAggregate;
  /** Cumulative cells the OCR returned with no dex number. */
  unknownCells: number;
  /** Cumulative cells observed (across all frames). */
  cellsObserved: number;
  /** Cumulative dex numbers parsed (across all frames). */
  numbersRead: number;
  /** Distinct variants the session has applied observations to. */
  variantsTouched: PokedexVariant[];
  /**
   * Build 3 / v1.4.6 b3 — distinct dex numbers buffered for the end-of-session
   * fallback because their frames were real grids but the variant never
   * resolved to high confidence. >0 here with `applied === 0` is what triggers
   * the "Detected N entries — apply to <Filter>?" confirmation instead of a
   * silent "no new entries". Reset to 0 once `finalizeFallback` flushes them.
   */
  bufferedFallback: number;
  /**
   * Build 3 / v1.4.6 b3 — true once the session resolved ANY high-confidence
   * variant frame (chip ≥ 0.6 or the legacy vote). When this stays false the
   * caller routes the buffered numbers through the Nexus-side filter fallback.
   */
  resolvedVariant: boolean;
  /** ms timestamp of the most recent ingested frame, or 0 if none. */
  lastFrameAt: number;
  /** Most recent frame's screen mode (drives diagnostics + PiP). */
  lastMode: DexScanMode;
  /** Most recent confidence number reported by the OCR. */
  lastConfidence: number;
  /** Most recent frame's variant, when known. */
  lastVariant: PokedexVariant | null;
  /** Most recent frame's region, when known. */
  lastRegion: string;
  /** Most recent frame's "low confidence" / "no numbers" hint. */
  lastNote?: string;
  /**
   * b13c.7 — context of the most recent committed write, for the live PiP
   * message ("+4 to Hoenn / Perfect (252,253,254,255)"). Null until the first
   * commit of the session.
   */
  lastCommit: DexLiveCommit | null;
}

/**
 * b13c.7 — a single streamed commit's user-facing context. `region` is derived
 * strictly from the committed dex ids (never the classifier), matching the
 * region-from-dexId single-source-of-truth rule.
 */
export interface DexLiveCommit {
  variant: PokedexVariant;
  /** Derived region of the committed ids (majority when a frame spans a boundary). */
  region: OcrRegion;
  /** Display label for `region`. */
  regionLabel: string;
  /** The dex ids committed in this frame, ascending. */
  ids: number[];
}

/**
 * Categorised counts of why frames / numbers did not apply. Surfaced in
 * the diagnostics panel so a tester can see WHY first-run coverage is what
 * it is, without the UI getting noisy.
 */
export interface GatedReasonCounts {
  /** Frame did not look like a Pokémon GO Pokédex grid. */
  not_pokedex_grid: number;
  /** Frame OCR confidence was below the gate floor. */
  low_confidence: number;
  /** iOS broadcast picker / status overlay / Nexus app frame. */
  overlay_frame: number;
  /** Number seen only once so far — awaiting a confirming frame. */
  unstable_number: number;
  /** Number skipped because a manual override owns that entry. */
  manual_override: number;
  /**
   * b13c.7 — cell dropped because its dexId-derived region contradicted the
   * OCR classifier region by a non-adjacent gap (wrong-region misread guard).
   */
  cross_check_drop: number;
  /**
   * b13c.7 — frame's variant had not yet held for 2 consecutive confident
   * (≥80) grid frames, so its cells were withheld this frame (transient
   * Shadow/Mega flash guard).
   */
  variant_unstable: number;
  /**
   * 2026-07-11 — cell dropped because its dex number sat far BELOW the running
   * scroll anchor: a digit-drop / region-jump mis-read (e.g. 0739→039 while
   * scrolling ~750). Forward/high numbers are never counted here.
   */
  scroll_window_drop: number;
}

/**
 * G-Max QA per-session aggregate. Counts the PR #461 watched gate markers and
 * the apply context so the downstream monitor can answer, without re-deriving
 * anything: "did any watched gate fire in this scan, AND did the scan also
 * apply ids?" (the user's `Gate plus apply` urgent-failure condition).
 *
 * The marker counts are SESSION-CUMULATIVE (summed over every frame). They are
 * pure observation: incrementing them never changes whether a write happens —
 * the gates in `dexLiveFrameAdapter` / the two-frame loop below already decided
 * that. `watchedGateFired` is the OR of the three frame-level markers plus the
 * two-frame-pending marker; `twoFrameConfirmed` is tracked alongside so the
 * monitor can tell a healthy confirm from a suppressed pending.
 */
export interface GmaxQaAggregate {
  /** Σ present-write blocked (invalid dex member) across frames. */
  invalidDexMember: number;
  /** Σ present-write blocked (gmax weak sprite evidence) across frames. */
  gmaxWeakSprite: number;
  /**
   * Σ gmax weak evidence deferred across frames — weak-but-plausible G-Max
   * present sightings the adapter emitted as "present" + `weakGmaxEvidence` and
   * routed to the stricter 3-frame staged confirmation instead of vetoing.
   */
  gmaxWeakDeferred: number;
  /**
   * Σ gmax weak evidence pending — weak (slot,id) sightings held this scan,
   * still building toward the WEAK_GMAX_FRAMES_REQUIRED streak. A subset of
   * twoFramePending tagged as weak.
   */
  gmaxWeakPending: number;
  /**
   * Σ gmax weak evidence confirmed — weak (slot,id) sightings that cleared the
   * stricter 3-frame streak and applied. A subset of twoFrameConfirmed.
   */
  gmaxWeakConfirmed: number;
  /** Σ frame present-writes blocked (overlay text) across frames. */
  overlayTextBlock: number;
  /** Σ (slot,id) present sightings held one more frame for corroboration. */
  twoFramePending: number;
  /** Σ (slot,id) present sightings that cleared two-frame corroboration. */
  twoFrameConfirmed: number;
  /**
   * Σ NON-weak present sightings whose corroboration streak was carried across a
   * slot shift this session — a same-id catch that reappeared at a different grid
   * slot after a small scroll, inheriting its prior (different-slot) streak
   * instead of resetting to sighting 1. Pure observation; never relaxes a gate.
   */
  crossSlotCarried: number;
  /**
   * True once ANY watched gate (invalid dex member, gmax weak sprite, overlay
   * text, or a two-frame-pending hold) has fired this session. This is the
   * left-hand side of the "gate + apply" urgent condition.
   */
  watchedGateFired: boolean;
}

export type DexFrameRejectionReason =
  | "unknown_mode"
  | "no_variant"
  | "no_numbers"
  | "variant_unstable"
  | DexFrameGateReason;

export interface DexLiveSessionFrameApplyResult {
  applied: number;
  skippedManual: number;
  /** True when the frame was rejected before the store was touched. */
  rejected: boolean;
  /** Human-readable reason a frame was rejected, for diagnostics. */
  rejectionReason?: DexFrameRejectionReason;
  /**
   * Build 3 / v1.4.6 b3 — true when the frame was a real Pokédex grid whose
   * dex numbers were buffered for an end-of-session fallback because the
   * variant could not be resolved this frame. The numbers are NOT applied yet;
   * `finalizeFallback` flushes them once a fallback variant is chosen.
   */
  buffered?: boolean;
  /**
   * Dex numbers that this frame transitioned from not-present to present in
   * the store, i.e. genuine new-to-dex adds. Excludes numbers that were
   * already "present" before the scan (those produce no store write) and
   * manual-override skips. Drives the scanner's rolling "Added" list.
   */
  newlyAdded?: number[];
  /**
   * Fully-recognised numbers the dex already had "present" before this frame
   * (2026-08-31). No store write happens for these; they exist so the PiP can
   * show a re-scan as already-registered chips instead of silence. Display
   * only — never feeds a gate, veto, or write.
   */
  alreadyPresent?: number[];
}

/**
 * The store-writer signature the session depends on. `presentIds` is optional
 * so a test fake (or an older writer) that returns only counts still satisfies
 * the type; the committed-only "Added" set falls back to the applied-count gate
 * when it is absent. The real `applyScanObservations` always returns it.
 */
export type ApplyObservationsFn = (
  variant: PokedexVariant,
  observations: Array<{ dexNumber: number; state: CellState }>,
) => { applied: number; skippedManual: number; presentIds?: number[] };

/**
 * Build 3 / v1.4.6 b3 — outcome of the end-of-session fallback flush.
 * `pending` is the count of buffered dex numbers that are still waiting for a
 * variant decision (i.e. the session never resolved a high-confidence variant
 * and the caller has not yet supplied a fallback). `applied`/`skippedManual`
 * mirror the store result once a fallback variant IS supplied.
 */
export interface DexLiveSessionFallbackResult {
  applied: number;
  skippedManual: number;
  /** Dex numbers flushed (deduped) under the fallback variant. */
  ids: number[];
}

/**
 * Construct a fresh live session. The session is in-memory and lives for
 * the duration of one Start Live Scan → Save / Stop cycle. Restart by
 * constructing a new one — there is no resume.
 */
export function createDexLiveScanSession(opts?: {
  /** Override the store writer for tests. */
  apply?: ApplyObservationsFn;
  /**
   * Push 10 — inject a telemetry recorder so the OBSERVE/COMMIT split and the
   * pipeline latency markers are captured. Defaults to a real recorder; pass a
   * recorder with a no-op `log` in tests to keep output quiet. The recorder is
   * pure bookkeeping — it never changes which entries apply.
   */
  telemetry?: ScanTelemetry;
}) {
  const apply = opts?.apply ?? applyScanObservations;
  // Route the high-frequency [dex-scan]/[live-scan] telemetry into the in-memory
  // capture buffer and OFF the native console bridge (installed once, lazily,
  // after Capacitor has patched console). This is what stops the log flood from
  // freezing the Xcode console / starving the frame loop; export the buffer via
  // Settings → "Export scan log". See dexScanLog.ts.
  installDexScanLogTee();
  const telemetry = opts?.telemetry ?? createScanTelemetry();
  const totals: DexLiveSessionTotals = {
    framesObserved: 0,
    framesApplied: 0,
    applied: 0,
    skippedManual: 0,
    gated: {
      not_pokedex_grid: 0,
      low_confidence: 0,
      overlay_frame: 0,
      unstable_number: 0,
      manual_override: 0,
      cross_check_drop: 0,
      variant_unstable: 0,
      scroll_window_drop: 0,
    },
    gmaxQa: {
      invalidDexMember: 0,
      gmaxWeakSprite: 0,
      gmaxWeakDeferred: 0,
      gmaxWeakPending: 0,
      gmaxWeakConfirmed: 0,
      overlayTextBlock: 0,
      twoFramePending: 0,
      twoFrameConfirmed: 0,
      crossSlotCarried: 0,
      watchedGateFired: false,
    },
    unknownCells: 0,
    cellsObserved: 0,
    numbersRead: 0,
    variantsTouched: [],
    bufferedFallback: 0,
    resolvedVariant: false,
    lastFrameAt: 0,
    lastMode: "unknown",
    lastConfidence: 0,
    lastVariant: null,
    lastRegion: "unknown",
    lastNote: undefined,
    lastCommit: null,
  };

  // b13c.7 — variant 2-frame stability gate. A frame's variant may only commit
  // once it has been the same across the most recent 2 qualifying (grid_4x4,
  // confidence ≥ 80) frames. Guards against a single-frame Shadow/Mega flash
  // during a panel swipe writing a phantom entry.
  const variantStability: VariantStabilityTracker = createVariantStabilityTracker();

  // Per-variant APPLIED dedupe: dex numbers we have already written to the
  // store (or that hit a manual override). Keeps `applied`/`skippedManual`
  // from drifting when the camera holds on the same screen.
  const appliedByVariant = new Map<PokedexVariant, Set<number>>();

  // perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY set. Device log 2026-07-06
  // (iPhone 15 Pro, ff5f7cb) recorded 2839 cell-classify passes for 154 applied
  // writes (~18 classifications per successful apply). Once a cell has committed
  // to the store, re-classifying it yields no new signal. This set holds the
  // composite key `${variant}:${region}:${dexId}` of every cell that committed
  // this session (via instant-apply OR a completed pending streak). The frame
  // adapter consults it (through the session's isCellApplied) to SKIP the
  // expensive per-cell sprite classification for applied cells, while still
  // reading the dex number + slot geometry that produce the key.
  //
  // It is CLEARED on: session restart (fresh session -> empty set), any scan-
  // context reset (region / colour / variant tab change, via resetConfirmations),
  // and a scroll-direction REVERSE (the user scrolling back may want re-verify).
  const appliedThisSession = new Set<string>();
  // Composite key builder for appliedThisSession. Region is normalised to a
  // string so "unknown"/lite regions key consistently with the apply site.
  const appliedKeyFor = (
    v: PokedexVariant | null,
    region: string | null | undefined,
    dexId: number,
  ): string => `${v ?? "null"}:${region ?? "unknown"}:${dexId}`;
  // The context (variant + region) the applied set's keys currently belong to.
  // Updated each grid frame from `result`. Because the set is cleared on every
  // context change, all live keys share this context, so isCellApplied(dexId)
  // can reconstruct the key from it without the caller knowing the variant.
  let appliedContextVariant: PokedexVariant | null = null;
  let appliedContextRegion: string | null = null;
  // Scroll-direction tracking for the resurrect-on-reverse rule. We do not have
  // boxRowFlow's row-flow model wired into the live dex scan, so the equivalent
  // scrollDelta is derived from the sequence of per-frame LEAD (minimum) dex ids:
  // a rising lead id means scrolling forward/down, a falling lead id means the
  // user scrolled BACK. `lastScrollSign` holds the last non-zero direction; a
  // sign flip (positive -> negative or vice-versa) is the reverse trigger.
  let lastLeadDexId: number | null = null;
  let lastScrollSign = 0;
  // Scroll-position plausibility anchor (2026-07-11). Distinct from lastLeadDexId
  // (which can be dragged down by a mis-read): this only ever advances from the
  // KEPT cells, so a digit-drop outlier (0739→039) can't corrupt it. Numbers far
  // BELOW it are vetoed as mis-reads; forward/high numbers always pass. See
  // dexScrollWindow.ts.
  let scrollAnchor: number | null = null;
  // SETTLE WINDOW (device trace 2026-07-20). Pokémon GO moves the grid by
  // itself: when it tucks the top menu away the list jumps under the user's
  // finger, and the frames captured during that jump show one row's numbers
  // against another row's sprites. Reading them is fine — writing from them is
  // how #98/#99 were applied as caught from a single frame after ten frames of
  // reading them as uncaught silhouettes. While this counter is above zero the
  // session still reads frames (coverage, guidance, telemetry all stay live)
  // but commits nothing; it drains one per calm frame, so a scan that is simply
  // being scrolled steadily never notices it.
  let settleFramesRemaining = 0;

  // Per-variant STABILITY tracking: how many distinct frames have shown
  // each (still-unapplied) dex number. A number applies only once it has
  // been corroborated across STABLE_FRAMES_REQUIRED frames (or in one
  // frame when confidence + signature are both strong). This is the core
  // defence against a single bad frame applying spurious numbers.

  // Build 6 / Problem #1 — STICKY-VARIANT CONTEXT HOLD. The last variant the
  // session confidently routed through the gate, plus how many consecutive
  // unknown/variant-null frames we have held it through. A transient unknown
  // frame keeps `stickyVariant`'s in-flight pending streaks alive (rather than
  // letting context flicker drop them) until STICKY_VARIANT_HOLD_FRAMES is
  // exhausted, at which point the variant is treated as genuinely lost and its
  // pending streaks expire. A genuine variant_changed switch (a DIFFERENT
  // variant resolves) clears the sticky pointer immediately — the new variant
  // owns its own pending map, so we never apply into the old variant's context.
  let stickyVariant: PokedexVariant | null = null;
  let stickyHoldFrames = 0;
  // Vision-only trial: sticky last-known pill-variant cache, one per session.
  // With Tesseract retired and pill OCR routed to Apple Vision on a raw crop, a
  // marginal frame can return an EMPTY pill read even though the active variant
  // is unchanged. This cache re-applies the last confidently-read variant on
  // such a frame and invalidates on region change, hue swing > 25°, or two
  // consecutive different-variant reads. It complements the existing sticky-hold
  // pending-streak logic below (which governs streak preservation); this governs
  // the per-frame VARIANT the frame is routed under.
  const stickyPillCache = createStickyPillCache();
  // perf/scan-efficiency (2026-07-06): per-session pill-OCR pHash cache. The
  // bottom filter-pill row is screen-fixed while scrolling, so its Vision OCR is
  // paid for on pixel-identical input across consecutive frames (1318 Vision
  // pill-OCR calls in the 2026-07-06 iPhone 15 Pro trace). This cache stores the
  // Vision result against a perceptual hash of the pill ROI; extractPokedexFrame
  // (via the hook) reads it and short-circuits Vision on an unchanged ROI. It is
  // reset on EVERY scan-context reset (region / colour / variant tab change) via
  // resetConfirmations below — the same points stickyPillCache invalidates — and
  // starts empty on each new session (session restart). It is NOT reset on scroll
  // (resetConfirmations is not called then) because the pill is screen-fixed.
  const pillOcrCache: PillOcrCache = createPillOcrCache();
  // Root Cause D — consecutive good (gate+stability-cleared) frames the current
  // resolved variant has held. Drives the single-frame fast-path's "firmly held"
  // gate. Reset to 1 on a (re)lock to a different variant; incremented when a
  // good frame keeps the same variant. Sticky-hold flicker frames never touch it
  // (Root Cause A hysteresis preserves firm-lock status across the gaps).
  let variantLockRunFrames = 0;

  // b17a — NOT-CAUGHT reconciliation collection.
  //
  // A cell the OCR read as explicitly NOT caught (a silhouette was visible)
  // arrives as state "missing" (see dexLiveFrameAdapter). We collect these so
  // `finish()` can reconcile them against the user's manual "caught" marks and
  // surface gaps. Collection is deliberately conservative:
  //   - Only frames that passed the gate contribute (a real grid read).
  //   - A (variant, dexNumber) must be seen not-caught across the same
  //     STABLE_FRAMES_REQUIRED frames as a caught apply, so a single misread
  //     can never suggest un-catching. We track sightings + the best (highest)
  //     frame confidence seen, then the reconciliation engine applies the
  //     HIGH-confidence gate.
  //   - If a cell is EVER read as caught (present) this session, it is removed
  //     from the not-caught set — a later "not caught" frame is just OCR noise
  //     and must not produce a phantom gap for something we positively saw.
  const notCaughtSightings = new Map<string, number>();
  const notCaughtBest = new Map<string, NotCaughtReading>();
  const caughtThisSession = new Set<string>();
  // Evidence-accumulation commit (2026-07). Per (variant, dexId) caught/contra
  // votes accrued across the frames the cell is on screen. Keyed by dexId (NOT
  // slot) so it survives scrolling; reset only on scan-context change
  // (resetConfirmations). See EVIDENCE_MIN_VOTES / EVIDENCE_CONTRA_RATIO.
  const cellEvidence = new Map<string, CellEvidence>();
  const makeEvidence = (): CellEvidence => ({
    strong: 0,
    weak: 0,
    contra: 0,
    frames: 0,
    held: 0,
    slot: null,
    last: null,
    lastReason: null,
  });
  // Held cells survive a scan-context reset (which clears `cellEvidence`) so
  // the scan-end summary covers the whole session, not just the last region.
  const heldArchive = new Map<string, { variant: PokedexVariant; dexId: number; ev: CellEvidence; notCaughtReads: number }>();
  function archiveHeldCells(): void {
    for (const [k, ev] of Array.from(cellEvidence)) {
      if (ev.frames === 0) continue;
      const sep = k.indexOf(":");
      const variant = k.slice(0, sep) as PokedexVariant;
      const dexId = Number(k.slice(sep + 1));
      if (appliedByVariant.get(variant)?.has(dexId)) continue;
      heldArchive.set(k, { variant, dexId, ev, notCaughtReads: notCaughtSightings.get(k) ?? 0 });
    }
  }

  // Reader self-heal: accumulates blocked Shadow/Purified reads across frames
  // and drains the high-confidence, multi-frame-confirmed ones at session end.
  const selfHeal = new ShadowRosterSelfHealTracker();

  function nckey(variant: PokedexVariant, dexNumber: number): string {
    return `${variant}:${dexNumber}`;
  }

  // Build 3 / v1.4.6 b3 — end-of-session fallback buffer.
  //
  // Frames that ARE real Pokédex grids (they pass `gateDexLiveFrame`) but whose
  // PoGo variant chip never resolved to high confidence still carry valid dex
  // numbers. Build 2 dropped them outright, producing the "78 cell-ocr hits but
  // 0 apply observations → no new entries to apply" regression. We instead
  // accumulate their (deduped) dex numbers here. At session-end, if the session
  // never resolved a confident variant, the caller flushes this buffer to a
  // fallback variant (the Nexus-side selected filter) WITH an explicit user
  // confirmation — never silently, and never mid-scan.
  const fallbackBuffer = new Set<number>();
  // When Pokédex chrome (region / filter / progress) was last read, so a
  // header lost to one blurred scroll frame does not gate a real grid frame.
  let lastChromeAt = 0;
  const CHROME_LOCK_MS = 4000;
  let gateHeldCount = 0;

  function touchVariant(variant: PokedexVariant) {
    if (!totals.variantsTouched.includes(variant)) {
      totals.variantsTouched.push(variant);
    }
  }

  function getOrCreate<K, V>(map: Map<K, V>, key: K, make: () => V): V {
    let v = map.get(key);
    if (!v) {
      v = make();
      map.set(key, v);
    }
    return v;
  }

  // b13c.7 — region of a set of committed ids, derived strictly via
  // dexIdToRegion (never the classifier). Majority wins; ties resolve to the
  // lowest id's region since `ids` is ascending. "unknown" only when empty.
  function majorityDerivedRegion(ids: number[]): OcrRegion {
    if (ids.length === 0) return "unknown";
    const counts = new Map<OcrRegion, number>();
    for (const id of ids) {
      const r = dexIdToRegion(id);
      counts.set(r, (counts.get(r) ?? 0) + 1);
    }
    let best: OcrRegion = dexIdToRegion(ids[0]);
    let bestCount = counts.get(best) ?? 0;
    for (const id of ids) {
      const r = dexIdToRegion(id);
      const c = counts.get(r) ?? 0;
      if (c > bestCount) {
        best = r;
        bestCount = c;
      }
    }
    return best;
  }

  /**
   * Build 3 / v1.4.6 b3 — buffer a variant-unresolved frame's dex numbers for
   * the end-of-session fallback. Only frames that pass `gateDexLiveFrame` (a
   * real Pokédex grid, not an overlay/banner/low-confidence read) are buffered,
   * so a stray-digit broadcast banner can never seed the fallback. Counters
   * (`cellsObserved`/`numbersRead`) are deliberately NOT touched here — those
   * track APPLIED frames; the buffer only feeds the optional fallback flush.
   */
  function bufferFallbackObservations(result: DexLiveFrameResult) {
    if (result.observations.length === 0) return;
    const gate = gateDexLiveFrame(result);
    if (!gate.accept) {
      // Tally the reason so diagnostics still explain why a frame was ignored.
      if (gate.reason === "overlay_frame") totals.gated.overlay_frame += 1;
      else if (gate.reason === "low_confidence") totals.gated.low_confidence += 1;
      else if (gate.reason !== "no_numbers") totals.gated.not_pokedex_grid += 1;
      return;
    }
    for (const o of result.observations) {
      // b13i Part B / b17a: only buffer cells the adapter judged CAUGHT
      // (present). Uncaught silhouettes arrive as state "missing" (b17a) and
      // must never seed the end-of-session fallback flush, or they would be
      // re-applied as caught.
      if (o.state !== "present") continue;
      fallbackBuffer.add(o.dexNumber);
    }
    totals.bufferedFallback = fallbackBuffer.size;
  }

  function ingest(result: DexLiveFrameResult, frameTs: number = Date.now()): DexLiveSessionFrameApplyResult {
    totals.framesObserved += 1;
    // 1-based frame index for the telemetry latency markers. `framesObserved`
    // is bumped per ingested frame, so it doubles as a deterministic frame clock.
    const frameIndex = totals.framesObserved;
    totals.lastFrameAt = frameTs;
    totals.lastMode = result.mode;
    totals.lastConfidence = result.confidence;
    totals.lastVariant = result.variant;
    totals.lastRegion = result.region;
    totals.lastNote = result.note;

    // OBSERVE-phase latency marker: OCR produced usable numbers this frame, no
    // matter what the downstream commit gates decide. Fires before any gate.
    telemetry.noteNumbersRead(frameIndex, result.numbersRead);
    // ACQUIRE-phase latency marker: a frame that carries a resolved variant on a
    // real grid is the session's evidence that the page/context is locked. The
    // dedicated context tracker (dexScanContext) owns the fuller acquire state
    // machine; here we record the first frame the session itself routed a variant.
    if (result.mode === "grid_4x4" && result.variant) {
      telemetry.noteContextLocked(frameIndex);
    }

    // Vision-only trial: sticky last-known pill variant. Observe this frame's
    // Vision pill read; when the read is empty (marginal frame) but a cached
    // variant is still valid (same region, no large hue swing), adopt it so a
    // dropped read doesn't stall the scan. Region change / hue swing > 25° / two
    // consecutive different reads invalidate. Only applied on grid frames; the
    // frameHue signal is not threaded here yet, so it is null (region + the
    // consecutive-different debounce still guard against a stale carry).
    {
      const decision = stickyPillCache.observe({
        variant: result.variant,
        // variantConfidence is 0..1 from the adapter's two-signal vote; the cache
        // gate is on the 0..100 scale, so scale up.
        confidence: Math.round((result.variantConfidence ?? 0) * 100),
        region: result.region as unknown as PokedexRegion,
        frameHue: null,
        frameIdx: frameIndex,
      });
      recordPillOcrSource(decision.source);
      // eslint-disable-next-line no-console
      console.log("[dex-scan] pill-ocr sticky", {
        cached: stickyPillCache.current()?.variant ?? null,
        invalidated: decision.invalidated,
        source: decision.source,
      });
      // Adopt the sticky variant ONLY when this frame's own read was empty and
      // the cache supplied one — never override a fresh confident read.
      if (result.variant == null && decision.source === "sticky" && decision.variant != null) {
        result = { ...result, variant: decision.variant };
      }
    }

    // G-Max QA — accumulate this frame's watched present-write gate markers.
    // Done BEFORE any rejection return so a frame whose every present write was
    // vetoed (and then bounced as `no_numbers`) still contributes its markers.
    // Counting is pure: it never alters whether a write happens.
    const fm = result.gateMarkers;
    if (fm) {
      totals.gmaxQa.invalidDexMember += fm.invalidDexMember;
      totals.gmaxQa.gmaxWeakSprite += fm.gmaxWeakSprite;
      // gmaxWeakDeferred is NOT a veto — it is a candidate held for the stricter
      // staged confirmation, so it accumulates for context but must NOT trip
      // watchedGateFired (that flag is the left-hand side of the "gate + apply"
      // urgent condition; a healthy deferred catch is not a false-write risk).
      totals.gmaxQa.gmaxWeakDeferred += fm.gmaxWeakDeferred;
      totals.gmaxQa.overlayTextBlock += fm.overlayTextBlock;
      if (fm.invalidDexMember > 0 || fm.gmaxWeakSprite > 0 || fm.overlayTextBlock > 0) {
        totals.gmaxQa.watchedGateFired = true;
      }
      // Feed blocked Shadow/Purified reads into the self-heal tracker. Pure:
      // never alters whether a write happens this frame.
      if (fm.blockedShadowReads.length > 0) {
        selfHeal.recordFrame(fm.blockedShadowReads);
      }
    }

    // ── Pre-apply gate ──────────────────────────────────────────────
    // Reject overlay / broadcast-picker / non-grid frames before any
    // number can mutate state, and tally the reason for diagnostics.
    //
    // Build 3 / v1.4.6 b3 — when the frame is a real Pokédex grid (it carries
    // dex numbers and passes `gateDexLiveFrame`) but the variant could not be
    // resolved this frame, we BUFFER its dex numbers for the end-of-session
    // fallback rather than dropping them. This is the fix for the zero-apply
    // regression: an unknown variant must not nuke otherwise-valid observations.
    if (result.mode !== "grid_4x4" || !result.variant) {
      // Root Cause A (2026-06-24, round 4) — HELD-VARIANT RE-ROUTE. The highest-
      // leverage loss: the variant lock collapses to null on >50% of mid-scroll
      // frames, and the prior sticky-hold only PRESERVED in-flight streaks — it
      // never advanced them, so a real grid frame whose variant flickered to null
      // committed nothing and the Purified scan applied 4/168. When THIS frame is
      // a real grid (mode grid_4x4) that merely lost its variant, and we hold a
      // recent high-confidence sticky lock still within the hysteresis budget, we
      // ADOPT the held variant for this frame and fall through to the normal
      // confirmation pipeline — so its catches keep committing under the held
      // variant instead of dropping as mode:unknown. A genuine variant switch is
      // unaffected (a DIFFERENT variant resolves → the relock path below). The
      // adopted frame still passes every downstream gate (frame gate, variant
      // stability, region cross-check, 2/3-frame corroboration), so the re-route
      // preserves the strict write discipline; it only stops discarding the read.
      if (
        result.mode === "grid_4x4" &&
        !result.variant &&
        stickyVariant != null &&
        stickyHoldFrames < STICKY_VARIANT_HOLD_FRAMES &&
        result.observations.length > 0
      ) {
        stickyHoldFrames += 1; // count the flicker frame against the hold budget
        // eslint-disable-next-line no-console
        console.log("[dex-scan] variant flicker re-routed (sticky hold)", {
          stickyVariant,
          heldFrames: stickyHoldFrames,
          budget: STICKY_VARIANT_HOLD_FRAMES,
          observations: result.observations.length,
        });
        // Adopt the held variant for this frame and continue the normal pipeline.
        result = { ...result, variant: stickyVariant };
      } else {
      bufferFallbackObservations(result);
      const reason: DexFrameRejectionReason =
        result.mode !== "grid_4x4" ? "unknown_mode" : "no_variant";

      // Build 6 / Problem #1 — STICKY-VARIANT CONTEXT HOLD. A transient unknown
      // frame must NOT drop the in-flight confirmation streaks of the variant we
      // were just scanning. While we have a recent locked variant and are still
      // within the hold budget, we HOLD context: the sticky variant's pending
      // streaks are preserved untouched (no reset, no decrement, no apply) so a
      // few flicker frames between two good reads still let a streak accumulate.
      // Only once the flicker outlasts the budget do we treat the variant as
      // genuinely lost and EXPIRE its pending streaks. Holding only preserves
      // existing evidence; it can never create a write.
      if (stickyVariant) {
        if (stickyHoldFrames < STICKY_VARIANT_HOLD_FRAMES) {
          stickyHoldFrames += 1;
          // eslint-disable-next-line no-console
          console.log("[dex-scan] scan context held (sticky variant)", {
            stickyVariant,
            heldFrames: stickyHoldFrames,
            budget: STICKY_VARIANT_HOLD_FRAMES,
            reason,
          });
        } else {
          // Flicker outlasted the hold budget — context is genuinely lost.
          // Drop the sticky variant so a stale routing hint can never carry into
          // a fresh, unrelated read.
          // eslint-disable-next-line no-console
          console.log("[dex-scan] scan context lost (sticky hold expired)", {
            stickyVariant,
            heldFrames: stickyHoldFrames,
            budget: STICKY_VARIANT_HOLD_FRAMES,
          });
          stickyVariant = null;
          stickyHoldFrames = 0;
        }
      }

      // Held: no variant resolved this frame. "no_context" is the frame-wide
      // reason the brief asks for when a frame produces no useful output for
      // want of a stable variant/context.
      telemetry.noteHeld(reason === "unknown_mode" ? "not_grid" : "no_context");
      return {
        applied: 0,
        skippedManual: 0,
        rejected: true,
        rejectionReason: reason,
        buffered: result.observations.length > 0,
      };
      }
    }
    if (result.observations.length === 0) {
      telemetry.noteHeld("no_numbers");
      return { applied: 0, skippedManual: 0, rejected: true, rejectionReason: "no_numbers" };
    }

    if (hasPokedexChrome(result)) lastChromeAt = frameTs;
    const gate = gateDexLiveFrame(result, {
      chromeRecentlySeen: lastChromeAt > 0 && frameTs - lastChromeAt < CHROME_LOCK_MS,
    });
    if (!gate.accept) {
      // Into the export, every 10th held frame, so a page that "does not
      // scan" shows WHY in the log (2026-09-22: the 100% / XXL pages).
      gateHeldCount += 1;
      if (gateHeldCount % 10 === 1) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] gate held", {
          reason: gate.reason,
          numbersRead: result.numbersRead,
          region: result.region,
          variantSource: result.variantSource,
          regionKnown: result.gridSignature?.regionKnown ?? null,
          filterKnown: result.gridSignature?.filterKnown ?? null,
          progressInRange: result.gridSignature?.progressInRange ?? null,
          strength: gate.signatureStrength,
        });
      }
      if (gate.reason === "overlay_frame") totals.gated.overlay_frame += 1;
      else if (gate.reason === "low_confidence") totals.gated.low_confidence += 1;
      else if (gate.reason === "no_numbers") {
        // already handled above, but keep the contract explicit
      } else totals.gated.not_pokedex_grid += 1;
      telemetry.noteHeld(
        gate.reason === "overlay_frame"
          ? "overlay_text"
          : gate.reason === "low_confidence"
            ? "low_confidence"
            : gate.reason === "no_numbers"
              ? "no_numbers"
              : "not_grid",
      );
      return { applied: 0, skippedManual: 0, rejected: true, rejectionReason: gate.reason };
    }

    // b13c.6 — route by THIS frame's variant. Each variant keeps its own
    // applied-dedupe + pending-stability slots, so a scroll through several
    // PoGo filters commits each variant's catches independently. There is no
    // cross-variant seeding or debounce: a frame only ever touches the slot for
    // the variant its own classifier reported, which is exactly what the
    // sticky-lock removal lets through.
    //
    // result.variant is guaranteed non-null here: either the frame resolved its
    // own variant, or the Root Cause A re-route above adopted the (non-null) held
    // sticky variant. The reassignment in that re-route defeats TS's flow
    // narrowing, so we assert it.
    const variant = result.variant as PokedexVariant;

    // perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY context + scroll tracking.
    // Keep the applied-set's context (variant + region) current for isCellApplied,
    // and detect a scroll-direction REVERSE so a user scrolling back re-verifies
    // cells that were previously dumped. The scrollDelta proxy is the change in
    // this frame's LEAD (minimum) dex id vs the previous grid frame's; a sign flip
    // vs the last non-zero direction clears the applied set. This runs BEFORE the
    // variant-stability early-return so context/scroll state stay live even on a
    // held frame (the frame still reflects the on-screen page).
    appliedContextVariant = variant;
    appliedContextRegion = (result.region as unknown as string | null) ?? null;
    if (result.observations.length > 0) {
      let leadDexId = result.observations[0].dexNumber;
      for (const o of result.observations) {
        if (o.dexNumber < leadDexId) leadDexId = o.dexNumber;
      }
      if (lastLeadDexId != null) {
        const delta = leadDexId - lastLeadDexId;
        // Is this frame the aftermath of a JUMP rather than a scroll? Two
        // shapes, both of which the pixel-level stability gate can miss (it
        // samples a header band that PoGo's menu tuck barely disturbs — on the
        // #98/#99 frame it measured a diff of 1.07 out of a threshold of 8):
        //   - the grid went BACKWARDS (menu untuck, or the user flicking back),
        //   - or it leapt further than a scroll plausibly moves between frames.
        // Either way the very next frames are blends, so open the settle window.
        const reversed = delta !== 0 && lastScrollSign !== 0 && Math.sign(delta) !== lastScrollSign;
        const leapt = Math.abs(delta) > SETTLE_JUMP_MAX_IDS;
        if (reversed || leapt) {
          settleFramesRemaining = reversed ? SETTLE_FRAMES_AFTER_REVERSE : SETTLE_FRAMES;
          if (DEX_SCAN_VERBOSE) {
            // eslint-disable-next-line no-console
            console.log("[dex-scan] scroll jump, settle window opened", {
              fromLeadDexId: lastLeadDexId,
              toLeadDexId: leadDexId,
              delta,
              reason: reversed ? "reverse" : "leap",
              settleFrames: settleFramesRemaining,
            });
          }
        }
        if (delta !== 0) {
          const sign = delta > 0 ? 1 : -1;
          if (lastScrollSign !== 0 && sign !== lastScrollSign && appliedThisSession.size > 0) {
            const clearedCount = appliedThisSession.size;
            appliedThisSession.clear();
            if (DEX_SCAN_VERBOSE) {
              // eslint-disable-next-line no-console
              console.log("[dex-scan] applied-set cleared (reason: scroll-reverse)", {
                clearedCount,
                fromLeadDexId: lastLeadDexId,
                toLeadDexId: leadDexId,
              });
            }
          }
          lastScrollSign = sign;
        }
      }
      lastLeadDexId = leadDexId;
    }

    // Scroll-window plausibility (2026-07-11, Lucky #039/#041/#044). Flag
    // backward-outlier numbers — a digit-drop / region-jump mis-read like
    // 0739→039 while scrolling ~750 — against the running scroll anchor. Runs on
    // EVERY grid frame (before the stability early-return) so the anchor stays
    // live even on a held frame; the keep-mask is consumed in the cross-check /
    // apply loop below. Forward / high numbers always pass, so sparse dexes
    // (shadow/purified/mega/gmax) are never throttled.
    const frameNumbers = result.observations.map((o) => o.dexNumber);
    const scrollAnchorBefore = scrollAnchor;
    const scrollWin = filterScrollBackwardOutliers(frameNumbers, scrollAnchor);
    scrollAnchor = scrollWin.nextAnchor;
    if (DEX_SCAN_VERBOSE && scrollWin.vetoedCount > 0) {
      // eslint-disable-next-line no-console
      console.log("[dex-scan] scroll-window veto", {
        anchor: scrollAnchorBefore,
        vetoed: frameNumbers.filter((_, i) => !scrollWin.keep[i]),
        keptAnchor: scrollWin.nextAnchor,
      });
    }

    // b13c.7 — VARIANT 2-FRAME STABILITY GATE. The frame's variant may only
    // commit once it has held across the most recent 2 qualifying (grid_4x4,
    // confidence ≥ 80) frames. A single-frame Shadow/Mega flash during a panel
    // swipe never clears this, so it can no longer write a phantom entry. The
    // frame still counts toward framesApplied/diagnostics below; we just hold
    // its cells until the variant corroborates.
    const stability = variantStability.observe({
      variant,
      mode: result.mode,
      confidence: result.confidence,
    });
    // The gate only BLOCKS a frame that qualified for it (grid_4x4, confidence
    // ≥ 80) but whose variant has not yet held for 2 consecutive qualifying
    // frames — i.e. a confident single-frame variant flash. A frame that did
    // not qualify (e.g. a low-confidence weak read) is the gate's no-op and
    // falls through to the existing per-number stability logic untouched.
    if (stability.qualified && !stability.stable) {
      totals.framesApplied += 1;
      touchVariant(variant);
      totals.resolvedVariant = true;
      totals.gated.variant_unstable += 1;
      telemetry.noteHeld("variant_unstable");
      return {
        applied: 0,
        skippedManual: 0,
        rejected: true,
        rejectionReason: "variant_unstable",
      };
    }

    // Build 6 / Problem #1 — STICKY-VARIANT (re)lock. This frame resolved a
    // real, stability-cleared variant, so it (re)locks the sticky context and
    // refreshes the unknown-frame hold budget. We deliberately do NOT touch any
    // other variant's pending map here: a genuine variant switch is already safe
    // because each variant owns its own pending + applied maps, so a frame can
    // only ever apply into the variant its own classifier reported — never into
    // the old variant's context (the brief's variant_changed guarantee). This
    // per-variant isolation is also what lets a trainer legitimately alternate
    // filters (b13c.6) without one variant's streak nuking another's. The
    // variant_changed CONTEXT reset (routing/colour-family) is owned by the
    // dexScanContext tracker at the hook layer, which has the colour signal the
    // session lacks.
    if (stickyVariant !== null && stickyVariant !== variant) {
      // eslint-disable-next-line no-console
      console.log("[dex-scan] variant_changed (sticky relock)", {
        from: stickyVariant,
        to: variant,
      });
    }
    // Track the consecutive-clean-frame lock run for telemetry/reset accounting.
    variantLockRunFrames = stickyVariant === variant ? variantLockRunFrames + 1 : 1;
    stickyVariant = variant;
    stickyHoldFrames = 0;

    // b13c.7 — dexId-vs-region CROSS-CHECK. The write key is the region derived
    // from each dex id, never the classifier region. We still use the classifier
    // region as a sanity signal: a cell whose derived region is non-adjacent to
    // the on-screen region (and not the allowed Hisui-in-Galar interleave) is a
    // wrong-region misread and is dropped before it can be written.
    const crossChecked: typeof result.observations = [];
    result.observations.forEach((o, i) => {
      // Scroll-window veto first: a number far below the scroll anchor is a
      // digit-drop / region-jump mis-read (0739→039) and must never write, even
      // though its sprite is a genuine catch and its (mangled) number/region are
      // internally self-consistent — which is exactly why the region cross-check
      // and the 2-frame confirmation both miss it.
      if (!scrollWin.keep[i]) {
        totals.gated.scroll_window_drop += 1;
        // eslint-disable-next-line no-console
        console.log("[dex-scan] scroll-window drop", {
          dexId: o.dexNumber,
          scrollAnchor: scrollAnchorBefore,
        });
        return;
      }
      const check = crossCheckCell(o.dexNumber, result.region, variant);
      if (check.keep) {
        crossChecked.push(o);
      } else {
        totals.gated.cross_check_drop += 1;
        // eslint-disable-next-line no-console
        console.log("[dex-scan] cross-check drop", {
          dexId: o.dexNumber,
          derivedRegion: check.derivedRegion,
          classifierRegion: check.classifierRegion,
          reason: check.reason,
        });
      }
    });

    // b17a — partition the gated, cross-checked observations into the additive
    // CAUGHT stream ("present") and the not-caught reconciliation stream
    // ("missing"). The additive apply pipeline below only ever handles present
    // cells, exactly as before b17a; missing cells feed conflict collection and
    // never touch the store.
    const presentObs = crossChecked.filter((o) => o.state === "present");
    const missingObs = crossChecked.filter((o) => o.state === "missing");

    // OBSERVE phase (Push 10). A present observation that reached this point has
    // already cleared every per-cell write gate that can demote it to "missing":
    // the row fence, slot mapping, sprite-state veto, slot-range, roster
    // membership, overlay-text veto, OCR sanity, and the region cross-check. Its
    // number was therefore reliably READ in the correct context — which is the
    // definition of the row being SEEN — independently of whether the strict
    // two/three-frame corroboration gate below ever APPLIES the entry. This is
    // the key observe/commit separation the brief asks for: a G-Max row whose
    // ids OCR'd cleanly is marked observed here even when an invalid neighbour in
    // the same frame was demoted (it never reaches presentObs) and even when the
    // sprites are still building toward the stricter weak-G-Max streak below.
    // Marking a row observed NEVER applies an entry and NEVER relaxes a gate.
    const observedRowIds = new Map<string, number[]>();
    for (const o of presentObs) {
      const rk = rowKeyForYPct(o.yPct);
      const ids = observedRowIds.get(rk) ?? [];
      ids.push(o.dexNumber);
      observedRowIds.set(rk, ids);
    }
    for (const [rk, ids] of Array.from(observedRowIds)) {
      telemetry.observeRow({ variant, rowKey: rk, ids, frameIndex });
    }

    // NOTE (merge 2026-07-22): main's silhouette ledger (`silhouetteReads` /
    // `priorNotCaughtReads`) was banked here to feed the flip-flop guard below.
    // Both go with that guard — the evidence engine keeps the equivalent history
    // as per-cell contra votes. See docs/scan-merge-superseded-main-guards.md.

    // Record positively-caught ids so a later "not caught" misread for the same
    // entry can't fabricate a gap. Caught always wins within a session.
    for (const o of presentObs) {
      const k = nckey(variant, o.dexNumber);
      caughtThisSession.add(k);
      notCaughtSightings.delete(k);
      notCaughtBest.delete(k);
    }
    // Collect not-caught readings, tracking sightings + the best (highest) frame
    // confidence seen. A clean frame counts as instantly stable (matching the
    // caught-apply instant rule); the eligibility bar (instant OR
    // STABLE_FRAMES_REQUIRED sightings) is applied when the readings are
    // extracted at session-end, so a single misread can never suggest a gap.
    const conf = frameConfidence(result);
    const instantStable =
      result.confidence >= HIGH_CONFIDENCE_INSTANT &&
      gate.signatureStrength >= STRONG_SIGNATURE_INSTANT;
    for (const o of missingObs) {
      const k = nckey(variant, o.dexNumber);
      // Evidence: a not-caught read votes AGAINST a catch for this cell — but only
      // when the classifier actually SAW something. A flickering caught<->silhouette
      // cell is exactly the phantom the gate must block, and that vote is counted
      // unconditionally (even if the cell reads caught in other frames).
      //
      // `sprite-absent` is NOT that. It means the classifier found no body at all,
      // which on a scrolling page usually means PoGo drew the cell's number before
      // its sprite — an absence of evidence, not evidence of absence. Counting it
      // buried real catches under contra they could never out-vote: on the
      // 2026-07-23 device pass (169-catch labelled shiny GT) #520/#581/#587/#813
      // read empty 5/13/8/10 times at chroma 0.033-0.043 and never committed,
      // while #608 reached strong=1 and was blocked by a single contra. That was 6
      // of the 17 misses — recall 89.8%, with precision already at 100%, so the
      // contra was buying nothing.
      //
      // This is the distinction main's flip-flop guard drew and the evidence
      // merge flattened; see docs/scan-merge-superseded-main-guards.md. Gating on
      // spriteReason rather than spriteState because the state is the coarser
      // signal: "empty" is returned for BOTH sprite-absent and (on binary variants)
      // a flat body, and only the former is an absence of evidence.
      if (o.spriteReason === "silhouette-grey") {
        getOrCreate(cellEvidence, k, makeEvidence).contra += 1;
      }
      if (caughtThisSession.has(k)) continue; // positively caught earlier — not a gap
      // An instant-stable frame jumps straight to the required sighting count;
      // otherwise accumulate one sighting per corroborating frame.
      const prevSightings = notCaughtSightings.get(k) ?? 0;
      const sightings = instantStable
        ? Math.max(prevSightings + 1, STABLE_FRAMES_REQUIRED)
        : prevSightings + 1;
      notCaughtSightings.set(k, sightings);
      const prevBest = notCaughtBest.get(k);
      if (!prevBest || conf > prevBest.confidence) {
        notCaughtBest.set(k, { variant, dexNumber: o.dexNumber, confidence: conf });
      }
    }

    const appliedSet = getOrCreate(appliedByVariant, variant, () => new Set<number>());

    // SETTLE GATE. The grid moved on its own (or the trainer flicked); this
    // frame and its neighbours are blends of two rows, so they are read for
    // coverage and held to the STRICTEST write bar. The window drains here, on a
    // frame that reached the apply stage, so the next steady frame commits
    // normally — a jump costs one frame, not a stalled scan. Everything
    // downstream (cell counters, telemetry, coverage, PiP guidance) runs exactly
    // as it would on any read frame.
    //
    // "Strictest bar", not "no writes at all" (2026-07-21). The window used to
    // discard every observation on the theory that the settled frame behind it
    // would write them properly. That holds only while the cell is still on
    // screen: in the Kanto+Johto trace #180, #183 and #227 were seen ONCE, on a
    // settle frame, and the scroll carried them away before any other frame read
    // them — held, then gone. So a settle frame may still commit through the
    // `caught-state` tier, where the pixel classifier positively identified a
    // caught body, and nothing else. That distinction is exactly the one the
    // trace supports: #180 (spriteState "caught", conf 0.971) and #183 ("caught",
    // 1.0) are real catches, while #98 — the settle-window false write, one frame
    // after a reversal — read spriteState "silhouette" and stays blocked.
    const holdForSettle = settleFramesRemaining > 0;
    if (holdForSettle) {
      settleFramesRemaining -= 1;
      telemetry.noteHeld("scroll_settling");
      if (DEX_SCAN_VERBOSE) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] applies restricted (grid settling)", {
          variant,
          region: result.region,
          // Candidates, not held ids: each is still put through the apply loop
          // at the caught-state-only bar, and the per-cell `apply` /
          // `apply held … reason:"grid-settling"` lines below say which way it
          // went. Logging these as "held" is what made the 2026-07-20 trace look
          // like the window was swallowing whole rows wholesale.
          candidateIds: presentObs.map((o) => o.dexNumber),
          framesRemaining: settleFramesRemaining,
        });
      }
    }

    // Scan overhaul v2 — SINGLE-FRAME STREAMING APPLY in reading order. Apple
    // Vision reads dex numbers at confidence 1.0, so the Tesseract-era two-frame
    // corroboration engine (pending streaks, cross-slot carry, streak grace,
    // purified-ratio accounting, weak-Gmax staging) is DELETED: a present cell
    // that clears the single clean-frame bar below commits the instant one frame
    // reads it. Write correctness is held by the guards that do NOT depend on
    // re-reads — the scroll-stability gate (only stable, non-smeared frames reach
    // OCR), the ascending-neighbour OCR sanity pass, the region cross-check,
    // roster membership, and the PiP-overlap guard below.
    //
    // Commit order is the natural reading order the eye follows: top-to-bottom by
    // row (ascending yPct), then left-to-right within a row (ascending slotIndex,
    // which is 1-based row-major). A slow continuous scroll therefore STREAMS
    // applies row-by-row instead of forcing the trainer to stop and hold steady.
    // Sorting a shallow copy leaves `result.observations` untouched for the
    // diagnostic layer.
    const readingOrder = [...presentObs].sort((a, b) => {
      const dy = (a.yPct ?? 0) - (b.yPct ?? 0);
      if (Math.abs(dy) > ROW_BAND_EPSILON) return dy;
      return (a.slotIndex ?? 0) - (b.slotIndex ?? 0);
    });
    const ready: typeof result.observations = [];
    for (const o of readingOrder) {
      if (appliedSet.has(o.dexNumber)) continue; // already written this session
      // Per-frame QUALITY filter (unchanged eligibility). A pill-blended ROI (the
      // iOS fast-scroll capsule leaves too little sprite after the b18 mask), the
      // fragile chroma-only `local-presence` fallback, or a sub-threshold sprite
      // confidence casts NO vote — the cell simply waits for a clean frame rather
      // than polluting its evidence either way. This is the fast-scroll pill, NOT
      // the Nexus PiP (guarded by cell-skip in the OCR layer).
      const obsPillOverlap = (o as { pillOverlapFraction?: number }).pillOverlapFraction ?? 0;
      const spriteReason = (o as { spriteReason?: string }).spriteReason ?? null;
      const spriteConf = o.spriteConfidence ?? 0;
      // Chroma + foreground mass, surfaced at the DECISION point so the next
      // device pass shows whether a held "empty" cell is a small COLOUR catch
      // (real chroma + mass) or a genuinely empty grey slot. Not yet a gate —
      // the classify-vs-evidence gap is being characterised first.
      const spriteChroma = (o as { spriteChromaFraction?: number }).spriteChromaFraction ?? null;
      const spriteFg = (o as { spriteForegroundPixels?: number }).spriteForegroundPixels ?? null;
      const spriteFgFraction = (o as { spriteForegroundFraction?: number }).spriteForegroundFraction ?? null;
      // STRUCTURE SIGNAL (2026-07-22): lumStd/bodyFrac from classifyCellPresence,
      // logged at the decision point so the next device pass can separate a dim
      // same-hue catch from blank-cell background bleed where chroma/mass overlap
      // — the principled successor to EMPTY_STATE_APPLY_*_FLOOR. Telemetry only.
      const spriteBodyFrac = (o as { spriteBodyFrac?: number }).spriteBodyFrac ?? null;
      const spriteLumStd = (o as { spriteLumStd?: number }).spriteLumStd ?? null;
      const pillOverlapOk = obsPillOverlap <= PILL_OVERLAP_APPLY_MAX;
      const qualityOk =
        pillOverlapOk &&
        o.spriteVerdict === "caught" &&
        spriteConf >= SPRITE_APPLY_THRESHOLD &&
        spriteReason !== "local-presence";

      // EVIDENCE ACCUMULATION (2026-07 redesign). Fold this frame's caught read
      // into the running per-cell evidence, classified by how much the density and
      // pixel classifiers AGREE, then commit on the balance — never on one frame.
      const ev = getOrCreate(cellEvidence, nckey(variant, o.dexNumber), makeEvidence);
      ev.frames += 1;
      ev.slot = o.slotIndex ?? null;
      ev.last = {
        state: o.spriteState ?? null,
        conf: Number(spriteConf.toFixed(3)),
        chroma: spriteChroma,
        fg: spriteFg,
        body: spriteBodyFrac,
        lum: spriteLumStd,
      };
      if (qualityOk) {
        if (o.spriteState === "caught") {
          // Both classifiers agree the cell holds a caught body — the strongest
          // single-frame signal, authoritative even on a floor-confidence frame
          // (the pixel classifier needs no help from Vision to see a full sprite).
          // This is the old `caughtStateAuthoritative` fast path, preserved. The
          // OTHER old instant path — a Vision-clean frame committing a caught
          // VERDICT whose pixel state was NOT "caught" — is deliberately gone: that
          // was the empty/silhouette phantom hole (#7/#121), now routed to
          // contra/weak evidence instead of an instant write.
          ev.strong += 1;
        } else if (o.spriteState === "silhouette") {
          // Density says caught, pixels read a grey body — could be a dark/grey
          // real catch. A caught vote, but WEAK: it must clear the ratio, and a
          // true silhouette that also reads "missing" elsewhere accrues contra.
          ev.weak += 1;
        } else if (o.spriteState == null || o.spriteState === "none") {
          // No legacy pixel classification ran for this cell at all (the
          // cell crop was skipped: no number anchor / no canvas), so there is
          // nothing to agree or disagree with. Absence of evidence: a weak
          // vote when the presence classifier measured a structured body, no
          // vote otherwise. Counting it as contra buried Lucky Empoleon under
          // 11 contra from 11 "none" reads (2026-09-23 Galaxy log).
          if (structuredBodyOk(spriteBodyFrac, spriteLumStd)) ev.weak += 1;
        } else if (
          emptyStateSubstanceOk({
            chroma: spriteChroma,
            fgFraction: spriteFgFraction,
            fgPixels: spriteFg,
            bodyFrac: spriteBodyFrac,
            lumStd: spriteLumStd,
          })
        ) {
          // "empty" / "none" WITH SUBSTANCE: the pixel classifier saw no body, but
          // the region carries real colour or real mass. That is what a dim, small
          // or same-hue shiny sprite looks like to a classifier tuned for full
          // bodies, so it is a positive-but-unconfirmed vote, not evidence against.
          //
          // This is the pass-3 relaxation (57d7680) re-admitted through a door the
          // substance floor now holds shut behind it. When that relaxation was
          // first tried the floor did not exist, so "empty" was relaxed for EVERY
          // cell and the phantoms came with it. Splitting the case on substance is
          // what the labelled 11:06 pass measured: of 28 real catches held as
          // `contradicted`, 24 clear this bar — while of 33 correctly-rejected
          // non-catches held that run, only 3 do. An 8:1 separation on the exact
          // decision, rather than a blanket rule in either direction.
          ev.weak += 1;
        } else {
          // "empty" / "none" with NEITHER colour nor mass: a caught verdict over a
          // blank cell (device trace #7). Still counts AGAINST — this is the
          // density over-call the contra brake exists for, and the half of the
          // empty-state population that the labelled pass shows is overwhelmingly
          // not-caught.
          ev.contra += 1;
        }
      }

      const votes = ev.strong + ev.weak;
      const tier1 = ev.strong >= 1 && ev.contra === 0;
      // Tier 1b (2026-07 device pass): two frames where BOTH the density verdict
      // AND the pixel classifier independently read "caught" commit even with
      // contra present. A real catch that flickers to "empty" during scroll (the
      // pixel classifier under-reading a small/dark colour sprite in transit)
      // accrues contra that buries it under Tier 1's `contra === 0` and Tier 2's
      // 2× ratio — e.g. device #132 reached strong=7 yet was held behind
      // contra=12. The #7/#121 phantoms never earn a single strong vote (the
      // pixel classifier never cleanly agreed), so strong >= 2 cannot revive them.
      const tier1b = ev.strong >= 2;
      // Tier 2 gates the ambiguous middle at EVIDENCE_MIN_VOTES for every cell.
      //
      // Pass 4 raised the bar to 3 for pure-density commits (strong === 0) to stop
      // #144/#146, which committed on exactly weak=2 at chroma ~0.02–0.04 and mass
      // <1400. Reverted 2026-07-22: those cells are inside what the substance floor
      // below now blocks outright, so the extra vote bought no precision it wasn't
      // already getting — and it cost recall, because at the ~1.3 fps this pipeline
      // actually ingests, a cell scrolling past is on screen for about two ingested
      // frames. A 3-vote bar is unreachable for a large share of cells by geometry
      // alone: 14 of the 34 real catches missed in that pass never got a third look.
      // If a pure-weak bar is needed again, make it exposure-relative (a majority of
      // the frames the cell was actually visible in), not an absolute count.
      const minVotes = EVIDENCE_MIN_VOTES;
      // EMPTY-STATE SUBSTANCE GUARD. A pure-density commit whose committing frame
      // reads spriteState "empty" (the pixels saw NO body, only density voted)
      // must show real colour OR mass — otherwise it is a blank-cell over-call
      // (shiny 2fps pass: 16 false writes, all empty-state, chroma <=0.043 /
      // fg <=1358). Real dim catches clear it on colour or mass; a genuinely empty
      // cell does not. Only gates the strong===0 + "empty" case — a silhouette
      // read, a "none" read, or any pixel-confirmed (strong>=1) vote is unaffected.
      const emptyStatePureWeak = ev.strong === 0 && o.spriteState === "empty";
      const emptyStateSubstance =
        !emptyStatePureWeak ||
        emptyStateSubstanceOk({
          chroma: spriteChroma,
          fgFraction: spriteFgFraction,
          fgPixels: spriteFg,
          bodyFrac: spriteBodyFrac,
          lumStd: spriteLumStd,
        });
      const tier2 =
        votes >= minVotes &&
        votes >= ev.contra * EVIDENCE_CONTRA_RATIO &&
        emptyStateSubstance;
      if (!(tier1 || tier1b || tier2)) {
        // Not enough (or contradicted) evidence yet — hold. A clean stable catch
        // reaches Tier 1 on its first strong frame (no delay); only the ambiguous
        // middle waits a beat, and a flickering phantom never arrives.
        totals.gated.unstable_number += 1;
        const holdReason = !qualityOk
          ? !pillOverlapOk
            ? "pill-overlap"
            : spriteReason === "local-presence"
              ? "local-presence"
              : "unclean-read"
          : ev.contra > 0
            ? "contradicted"
            : "insufficient-evidence";
        ev.held += 1;
        ev.lastReason = holdReason;
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] apply held (evidence)", {
          variant,
          region: result.region,
          slotIndex: o.slotIndex ?? null,
          dexId: o.dexNumber,
          strong: ev.strong,
          weak: ev.weak,
          contra: ev.contra,
          spriteState: o.spriteState ?? null,
          spriteVerdict: o.spriteVerdict ?? null,
          spriteConfidence: Number(spriteConf.toFixed(3)),
          // Chroma + mass at the decision point: a held "empty" with high chroma
          // and real mass is a small colour catch we're missing; near-zero on
          // both is a genuinely empty slot correctly held.
          spriteChromaFraction: spriteChroma,
          spriteForegroundPixels: spriteFg,
          spriteBodyFrac,
          spriteLumStd,
          reason: holdReason,
        });
        continue;
      }

      // NOTE (merge 2026-07-22): main's FLIP-FLOP GUARD stood here — a cell with
      // a not-caught history had to be seen caught TWICE, on independent (non
      // cached) reads, before it could write. It is deliberately NOT carried over.
      // The evidence engine answers the same question with more information: a
      // silhouette history now accrues `contra`, which Tier 1 refuses outright and
      // Tier 2 must out-vote by EVIDENCE_CONTRA_RATIO, so the #120 case (thirteen
      // silhouette reads then one 0.979 sprite-present frame) is held by the
      // contra balance rather than by a separate counter. Keeping both would gate
      // an evidence-confirmed commit behind a second caught read and re-introduce
      // the false negatives that got the earlier sticky-not-caught guard reverted.
      // See docs/scan-merge-superseded-main-guards.md to restore it.
      ready.push(o);
      totals.gmaxQa.twoFrameConfirmed += 1;
      // eslint-disable-next-line no-console
      console.log("[dex-scan] apply", {
        variant,
        region: result.region,
        slotIndex: o.slotIndex ?? null,
        dexId: o.dexNumber,
        tier: tier1 ? "strong-instant" : tier1b ? "strong-confirmed" : "evidence",
        strong: ev.strong,
        weak: ev.weak,
        contra: ev.contra,
        spriteState: o.spriteState ?? null,
        spriteConfidence: Number(spriteConf.toFixed(3)),
        spriteChromaFraction: spriteChroma,
        spriteForegroundPixels: spriteFg,
        spriteBodyFrac,
        spriteLumStd,
      });
    }

    totals.framesApplied += 1;
    touchVariant(variant);
    // This frame resolved a real variant through the gate — the session no
    // longer needs the Nexus-side fallback at end-of-session.
    totals.resolvedVariant = true;

    // Cell/number counters accumulate ONLY for frames that passed the gate,
    // and each accepted frame contributes at most MAX_CELLS_PER_FRAME (16)
    // candidate cells. This is what keeps the diagnostics panel from claiming
    // a physically-impossible "379 cells" for what is a 16-cell 4×4 screen.
    const frameCells = Math.min(result.cellsObserved, MAX_CELLS_PER_FRAME);
    totals.cellsObserved += frameCells;
    totals.numbersRead += Math.min(result.numbersRead, MAX_CELLS_PER_FRAME);
    totals.unknownCells += Math.max(0, frameCells - Math.min(result.numbersRead, MAX_CELLS_PER_FRAME));

    // `ready` is already in reading order (top-to-bottom, left-to-right) from the
    // single-frame apply loop above — the natural order the cells were read this
    // frame — so we commit it as-is rather than re-sorting by dexNumber.

    if (ready.length === 0) {
      // The frame was a clean, accepted grid but committed nothing. Distinguish
      // the two candidate-local reasons so a debugger can tell a healthy hold
      // from a blocked one (neither is a frame-wide failure — the row may well
      // have been observed above):
      //   - presentObs non-empty → write-eligible cells exist but are still
      //     building toward the 2/3-frame corroboration streak.
      //   - presentObs empty → every candidate was demoted by a per-cell gate
      //     (membership / sprite / slot range / cross-check) before reaching the
      //     commit path; the row can still be observed-only.
      telemetry.noteHeld(
        presentObs.length > 0 ? "awaiting_corroboration" : "candidates_blocked",
      );
      return { applied: 0, skippedManual: 0, rejected: false };
    }

    // Snapshot which ready numbers are eligible to become a genuine new-to-dex
    // add: they must NOT already be "present" (an already-present number
    // produces no store write). A manual "not caught" cell IS eligible — under
    // last-write-wins the scan overwrites it, so flipping a manual `missing` to
    // present is a real new add. Everything that isn't already present is a
    // candidate; the committed-set intersection below keeps the announcement
    // honest when the store actually wrote it this frame.
    const newAddCandidates: number[] = [];
    // Fully-recognised numbers the dex ALREADY has (2026-08-31 ask): these
    // used to vanish silently, which read as "the scan isn't picking this
    // up" on a re-scan. Surfaced display-only so the PiP can show them as
    // already-registered chips. Free: the store read on this line already
    // happens for every ready cell — one array push, no extra I/O.
    const alreadyPresent: number[] = [];
    for (const o of ready) {
      const prev = getDexEntryState(variant, o.dexNumber);
      if (prev?.state === "present") {
        alreadyPresent.push(o.dexNumber);
        continue;
      }
      newAddCandidates.push(o.dexNumber);
    }
    const res = apply(variant, ready);
    // Committed-only "Added" set: a number is announced as added only when the
    // store CONFIRMS it wrote that number as "present" this frame. We intersect
    // the eligible new-add candidates with the store's committed present-id set
    // so a seen-only write or an unchanged re-observation can never be announced
    // as "Added". Older apply fns (and test fakes) that don't return presentIds
    // fall back to the candidate set gated on the applied count, matching prior
    // behaviour.
    const committedSet = res.presentIds != null ? new Set(res.presentIds) : null;
    const newlyAdded = committedSet
      ? newAddCandidates.filter((n) => committedSet.has(n))
      : res.applied > 0
        ? newAddCandidates
        : [];
    // Mark every attempted number as applied-for-dedupe regardless of
    // whether the store wrote it (an unchanged re-observation still counts as
    // "we've handled this number").
    for (const o of ready) appliedSet.add(o.dexNumber);
    // perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY. A cell that has committed
    // (instant-apply or a completed pending streak — both converge on `ready`)
    // will yield no new signal if re-classified, so record its composite key so
    // the frame adapter can SKIP its sprite classification next frame. Keyed on
    // this frame's committed context; the set is cleared whenever that context
    // changes (resetConfirmations) or the user scrolls back (scroll-reverse).
    for (const o of ready) {
      appliedThisSession.add(appliedKeyFor(variant, result.region as unknown as string | null, o.dexNumber));
    }
    totals.applied += res.applied;
    totals.skippedManual += res.skippedManual;
    totals.gated.manual_override += res.skippedManual;

    // b13c.7 — record the commit context for the live PiP message. Region is
    // derived from the committed ids (majority), NOT the classifier region, so
    // the pill reflects the single-source-of-truth write key. Only update when
    // the store actually wrote at least one entry.
    if (res.applied > 0) {
      const committedIds = ready.map((o) => o.dexNumber).sort((a, b) => a - b);
      const region = majorityDerivedRegion(committedIds);
      totals.lastCommit = {
        variant,
        region,
        regionLabel: dexRegionLabel(region),
        ids: committedIds,
      };
      // COMMIT phase (Push 10). Stamp the per-row commit latency so a debugger
      // can read the observe→commit gap directly. Group the just-committed cells
      // by their row band, mirroring the observe-phase grouping above, so the
      // recorder can mark the row committed and (if it was never explicitly
      // observed) back-fill the observation.
      const committedRowIds = new Map<string, number[]>();
      for (const o of ready) {
        const rk = rowKeyForYPct(o.yPct);
        const ids = committedRowIds.get(rk) ?? [];
        ids.push(o.dexNumber);
        committedRowIds.set(rk, ids);
      }
      for (const [rk, ids] of Array.from(committedRowIds)) {
        telemetry.commitRow({ variant, rowKey: rk, ids, frameIndex });
      }
    }
    return {
      applied: res.applied,
      skippedManual: res.skippedManual,
      rejected: false,
      newlyAdded,
      alreadyPresent,
    };
  }

  return {
    ingest,
    totals,
    /**
     * Scan-end diagnostic (2026-09-23): every cell the density verdict read as
     * caught that never committed, with its evidence balance, the pixel
     * classifier's last reading and the last hold reason. Heaviest-seen first,
     * capped at HELD_CELLS_SUMMARY_MAX. Pure read: no scan decision consults it.
     */
    heldCellsSummary(): HeldCellSummary[] {
      archiveHeldCells();
      const rows: HeldCellSummary[] = [];
      for (const { variant, dexId, ev, notCaughtReads } of Array.from(heldArchive.values())) {
        if (appliedByVariant.get(variant)?.has(dexId)) continue;
        rows.push({
          variant,
          dexId,
          slot: ev.slot,
          frames: ev.frames,
          held: ev.held,
          strong: ev.strong,
          weak: ev.weak,
          contra: ev.contra,
          notCaughtReads,
          lastState: ev.last?.state ?? null,
          lastConf: ev.last?.conf ?? 0,
          lastChroma: ev.last?.chroma ?? null,
          lastFg: ev.last?.fg ?? null,
          lastBody: ev.last?.body ?? null,
          lastLum: ev.last?.lum ?? null,
          reason: ev.lastReason,
        });
      }
      rows.sort((a, b) => b.frames - a.frames || a.dexId - b.dexId);
      return rows.slice(0, HELD_CELLS_SUMMARY_MAX);
    },
    /**
     * Stale-confirmation fix #1 — RESET INVALIDATION (2026-06-27).
     *
     * Clear ALL pending per-slot confirmation state on a scan-context reset
     * (`region_changed` / `colour_changed` / `variant_changed` / `left_dex_page`
     * / any other reason). A `slotIndex` is a GRID POSITION that the context
     * tracker remaps to new dexIds when the user scrolls to a new region/colour;
     * the per-slot confirmation buffers (sprite-match / histSim / confidence /
     * sighting count / two-frame pending lock) accumulated for the OLD cell must
     * NOT survive the reset to be inherited by the newly-remapped dexId — that is
     * exactly how an EMPTY remapped cell was applied with a stale perfect score on
     * a single post-reset sighting. Committed writes (`appliedByVariant`) are NOT
     * cleared: those are a permanent per-session dedupe, not pending state, and
     * clearing them would let an already-written id be re-applied. The sticky-
     * variant / firm-lock run counters are reset too so the single-frame fast-path
     * cannot re-engage on the first frame after a reset.
     */
    resetConfirmations(reason?: string): void {
      archiveHeldCells();
      notCaughtSightings.clear();
      notCaughtBest.clear();
      // Evidence-accumulation commit: a scan-context change (region / colour /
      // variant tab) means the on-screen cells are different, so their accrued
      // caught/contra votes no longer apply — start fresh.
      cellEvidence.clear();
      stickyVariant = null;
      stickyHoldFrames = 0;
      variantLockRunFrames = 0;
      // perf/scan-efficiency: a scan-context reset means the pill on the new
      // region/colour/variant page is a DIFFERENT capsule that could pHash-
      // collide with the previous one — invalidate the pill-OCR cache so the next
      // frame re-reads Vision rather than serving a stale variant result.
      pillOcrCache.reset();
      // perf/scan-efficiency (2026-07-06): a scan-context reset (region / colour /
      // variant tab change) means the applied set's composite keys no longer match
      // the on-screen context, so every cell must be eligible for classification
      // again. Clear the set and its scroll-direction memory.
      if (appliedThisSession.size > 0 && DEX_SCAN_VERBOSE) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] applied-set cleared (reason: session|variant|scroll-reverse)", {
          clearedCount: appliedThisSession.size,
          reason: reason ?? null,
        });
      }
      appliedThisSession.clear();
      lastLeadDexId = null;
      lastScrollSign = 0;
      settleFramesRemaining = 0;
      appliedContextVariant = null;
      appliedContextRegion = null;
      // eslint-disable-next-line no-console
      console.log("[dex-scan] confirmations reset", { reason: reason ?? null });
    },
    /**
     * perf/scan-efficiency: the per-session pill-OCR pHash cache handle, threaded
     * into extractPokedexFrame's options by the hook so the active-pill Vision
     * OCR is skipped on frames whose pill ROI is perceptually unchanged. Lifecycle
     * is owned here: created empty per session, reset on every scan-context reset
     * (resetConfirmations above). Callers must not hold this across sessions.
     */
    pillOcrCache(): PillOcrCache {
      return pillOcrCache;
    },
    /**
     * perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY query. Returns true when a
     * dex cell has already committed this session under the CURRENT scan context
     * (variant + region), so the frame adapter can skip its sprite classification.
     * The context is reconstructed from the last grid frame's committed variant +
     * region because the caller (adapter) only knows the dex number. The applied
     * set is cleared on every context change, so the current context always
     * matches every live key.
     */
    isCellApplied(dexNumber: number): boolean {
      return appliedThisSession.has(
        appliedKeyFor(appliedContextVariant, appliedContextRegion, dexNumber),
      );
    },
    /** Count of cells dumped this session (telemetry / tests). */
    appliedSetSize(): number {
      return appliedThisSession.size;
    },
    /**
     * Testable apply-mark: record a cell as committed under an explicit context.
     * The live path marks via the ingest apply chokepoint; this exists so tests
     * (and any external caller) can seed the set deterministically.
     */
    markCellApplied(dexNumber: number, variant: PokedexVariant | null, region: string | null): void {
      appliedContextVariant = variant;
      appliedContextRegion = region;
      appliedThisSession.add(appliedKeyFor(variant, region, dexNumber));
    },
    /**
     * Testable scroll-reverse trigger. Feeds a scroll delta (proxy for lead-dexId
     * change) into the reverse detector; a sign flip vs the last non-zero
     * direction clears the applied set, mirroring the in-ingest logic.
     */
    noteScrollDelta(delta: number): void {
      if (delta === 0) return;
      const sign = delta > 0 ? 1 : -1;
      if (lastScrollSign !== 0 && sign !== lastScrollSign && appliedThisSession.size > 0) {
        const clearedCount = appliedThisSession.size;
        appliedThisSession.clear();
        if (DEX_SCAN_VERBOSE) {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] applied-set cleared (reason: scroll-reverse)", {
            clearedCount,
          });
        }
      }
      lastScrollSign = sign;
    },
    /**
     * The grid is MOVING right now — open the settle window so the next frames
     * are read but not written. Called by the pre-OCR scroll-stability gate for
     * every frame it drops as transitional: those drops are the pixel-level
     * evidence of exactly the motion the settle window exists for, and the
     * frame that follows a drop is the blend that must not commit. The lead-id
     * jump detector inside `ingest` covers the motion this gate cannot see (a
     * menu tuck barely disturbs the header band it samples).
     */
    noteScrollMotion(): void {
      settleFramesRemaining = SETTLE_FRAMES;
    },
    /** Frames the settle window still has to hold (telemetry / tests). */
    settleFramesPending(): number {
      return settleFramesRemaining;
    },
    /** Clear the applied set (e.g. a manual re-scan request). */
    clearAppliedSet(): void {
      appliedThisSession.clear();
      lastLeadDexId = null;
      lastScrollSign = 0;
      settleFramesRemaining = 0;
    },
    /**
     * Drain the reader self-heal reports that crossed the confidence + frame
     * bar. Called once at session stop; each id fires at most once per session.
     */
    drainSelfHealReports(): SelfHealReport[] {
      return selfHeal.drain();
    },
    /**
     * How many cells currently hold PARTIAL evidence — at least one caught vote
     * accrued, not yet committed. These are the cells that would commit if the
     * scanner got one more look at them, which makes this the scan's DEMAND
     * signal: it is the only moment when re-reading a static screen is worth
     * spending power on.
     *
     * The frame intake uses it to decide whether a held-steady view earns a
     * forced re-read or should be left alone (see dexFrameDedup's `demand`).
     * Without it the intake re-OCRs a parked screen forever at a fixed cadence,
     * which is both the thermal cost and, when the cadence is too slow for the
     * cells that ARE waiting, the reason a held row needs a scroll to commit.
     *
     * Pure read — never mutates evidence.
     */
    pendingEvidenceCount(): number {
      let n = 0;
      for (const [key, ev] of cellEvidence) {
        if (ev.strong + ev.weak < 1) continue;
        const sep = key.indexOf(":");
        const variant = key.slice(0, sep) as PokedexVariant;
        const dexId = Number(key.slice(sep + 1));
        if (appliedByVariant.get(variant)?.has(dexId)) continue;
        n += 1;
      }
      return n;
    },
    /** Snapshot the totals — returns a stable copy callers can serialise. */
    snapshot(): DexLiveSessionTotals {
      return {
        ...totals,
        variantsTouched: [...totals.variantsTouched],
        gated: { ...totals.gated },
        gmaxQa: { ...totals.gmaxQa },
      };
    },
    /**
     * Push 10 — the pipeline telemetry snapshot: latency markers
     * (context-locked → first-numbers → first-row-observed → first-commit),
     * the observe-vs-commit row split (rows observed but never committed are
     * the load-bearing G-Max signal), and per-reason held-frame tallies. Pure
     * read; never mutates state. The diagnostics panel / QA monitor reads this
     * to see WHERE a scan spent its frames without re-deriving anything.
     */
    telemetrySnapshot(): ScanTelemetrySnapshot {
      return telemetry.snapshot();
    },
    /**
     * Push 11 — the session's telemetry recorder, exposed so the hook's
     * pre-context top-row buffer (which lives alongside the scan-context tracker
     * in useDexLiveScan, the only place with cross-frame OCR visibility) can
     * stamp its lifecycle markers (pre-context observed / promoted / expired /
     * variant-switch reset) onto the SAME session trace as every other marker.
     * Pure handle; recording a marker never applies an entry or relaxes a gate.
     */
    telemetryRecorder(): ScanTelemetry {
      return telemetry;
    },
    /**
     * Push 10b — flush the whole-session telemetry summary to the structured
     * console log (grep markers: `[dex-scan] telemetry snapshot`,
     * `… observe-vs-commit`, `… held reasons`). Called by the hook at session
     * end so the latency markers and observe/commit split land in a runtime
     * Xcode/TestFlight trace without a debug build. Pure read.
     */
    logTelemetrySnapshot(): void {
      telemetry.logSnapshot();
    },
    /**
     * b13c — record a frame the producers delivered but that was dropped
     * BEFORE OCR (e.g. the pre-OCR motion-blur gate in useDexLiveScan).
     *
     * This is the load-bearing fix for the b13a→b13c stuck-pill bug: the
     * pre-OCR gate `return`ed before `ingest`, so `framesObserved` never
     * moved off 0. `pipPhaseFromTotals` reads `framesObserved === 0` as
     * "no broadcast frames have arrived yet" and keeps the pill on
     * "Open your Pokédex" for the WHOLE session — even though frames were
     * flowing and merely being blur-dropped. By counting the observation
     * here we let the pill advance to the coaching state and we record the
     * drop under `gated` so diagnostics show WHY nothing applied.
     *
     * It deliberately does NOT touch lastMode / lastVariant / numbersRead:
     * a blur-dropped frame yielded no read, so those stay at their last
     * known values (or their "unknown" defaults).
     */
    noteFramePresented(frameTs: number = Date.now()): void {
      totals.framesObserved += 1;
      totals.lastFrameAt = frameTs;
      totals.gated.not_pokedex_grid += 1;
    },
    /**
     * Build 3 / v1.4.6 b3 — count of buffered dex numbers awaiting a fallback
     * decision. The caller checks this at session-end: if >0 (and the session
     * never resolved a confident variant) it prompts the user before flushing.
     */
    pendingFallbackCount(): number {
      return fallbackBuffer.size;
    },
    /** Distinct buffered dex numbers (sorted), for the confirmation prompt. */
    pendingFallbackIds(): number[] {
      return Array.from(fallbackBuffer).sort((a, b) => a - b);
    },
    /**
     * Build 13a — distinct dex numbers the session has actually applied to the
     * store, across every variant, sorted ascending. Drives the Review &
     * Confirm step's count + region (region is derived from these ids, not the
     * header text). Empty when nothing was committed.
     */
    appliedIds(): number[] {
      const all = new Set<number>();
      appliedByVariant.forEach((set) => {
        set.forEach((n) => all.add(n));
      });
      return Array.from(all).sort((a, b) => a - b);
    },
    /**
     * b17a — the stable NOT-CAUGHT readings collected this session, for the
     * reconciliation engine. A reading is returned only when:
     *   - it met the stability bar (instant-stable frame OR
     *     STABLE_FRAMES_REQUIRED corroborating sightings), and
     *   - the same (variant, dexNumber) was never read as caught this session.
     * Confidence is the best (highest) frame confidence seen for the entry; the
     * HIGH-confidence gate + manual-only conflict rule live downstream in
     * `reconcileNotCaughtReadings`. Returns a fresh array; never mutates state.
     */
    notCaughtReadings(): NotCaughtReading[] {
      const out: NotCaughtReading[] = [];
      notCaughtBest.forEach((reading, k) => {
        if (caughtThisSession.has(k)) return;
        if ((notCaughtSightings.get(k) ?? 0) < STABLE_FRAMES_REQUIRED) return;
        out.push(reading);
      });
      return out;
    },
    /**
     * Build 3 / v1.4.6 b3 — LAST-RESORT flush of the fallback buffer.
     *
     * Applies every buffered (variant-unresolved) dex number under
     * `fallbackVariant` — the Nexus-side currently-selected filter. This is
     * called ONLY at end-of-session, ONLY after the user confirms the prompt,
     * and ONLY when no high-confidence variant was detected during the scan.
     * It is never the per-frame default (that was the original Build 2 bug).
     * Idempotent: clears the buffer so a second call is a no-op.
     */
    finalizeFallback(fallbackVariant: PokedexVariant): DexLiveSessionFallbackResult {
      const ids = Array.from(fallbackBuffer).sort((a, b) => a - b);
      if (ids.length === 0) return { applied: 0, skippedManual: 0, ids: [] };
      const observations = ids.map((dexNumber) => ({
        dexNumber,
        state: "present" as const,
      }));
      const res = apply(fallbackVariant, observations);
      touchVariant(fallbackVariant);
      totals.applied += res.applied;
      totals.skippedManual += res.skippedManual;
      totals.gated.manual_override += res.skippedManual;
      fallbackBuffer.clear();
      totals.bufferedFallback = 0;
      return { applied: res.applied, skippedManual: res.skippedManual, ids };
    },
  };
}

export type DexLiveScanSession = ReturnType<typeof createDexLiveScanSession>;
